import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import logger from '../../utils/logger';
import { useAuth } from '../../contexts/AuthContext';
import { libraryApi } from '../../lib/api/libraryApi';
import useLibraryEditImpactConfirm from './useLibraryEditImpactConfirm';
import {
  XMarkIcon,
  SparklesIcon,
  PlusIcon,
  TrashIcon,
  Bars3Icon,
  CheckIcon,
  ClipboardDocumentListIcon,
  EnvelopeIcon,
  EyeIcon,
  ShareIcon,
  LinkIcon,
  DocumentDuplicateIcon,
  UserGroupIcon,
  LockClosedIcon,
  BuildingOffice2Icon,
  PlayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ShieldCheckIcon,
  MicrophoneIcon,
  VideoCameraIcon,
  PauseIcon,
} from '@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  DocumentTextIcon,
  CodeBracketIcon,
  BriefcaseIcon,
  DocumentMagnifyingGlassIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import {
  ADVANCED_INTERVIEW_TYPES,
  ADVANCED_INTERVIEW_TYPE_LABELS,
} from 'src/constants/advancedInterviewTypes';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { currentVoiceId } from '../../lib/replacedVoices';
import RubricModulesEditor from 'src/components/ai/RubricModulesEditor';

// Animation keyframes for question generation effect
const animationStyles = `
  @keyframes fadeSlideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .animate-fadeIn {
    animation: fadeSlideIn 0.3s ease-out forwards;
  }
`;

const CREATION_MODE = {
  AI: 'ai',
  MANUAL: 'manual',
};

const CREATOR_CHOICE = {
  SELF: 'self',
  EXTERNAL: 'external',
};



/** Interview question categories (matches `questionTags` ids and server `section` values). */
const QUESTION_CATEGORY_SECTIONS = ['eligibility', 'technical', 'behavioral', 'cultural'];

const defaultSectionFromInterviewTags = (selectedTags) => {
  if (!Array.isArray(selectedTags) || selectedTags.length === 0) return 'technical';
  const first = QUESTION_CATEGORY_SECTIONS.find((id) => selectedTags.includes(id));
  return first || 'technical';
};

const normalizeSectionForCategorySelect = (section) =>
  QUESTION_CATEGORY_SECTIONS.includes(section) ? section : 'technical';

/** Normalize stored expectedAnswer to "Yes" | "No" for yes/no questions (matches type-switch logic). */
const normalizeYesNoExpected = (answer) => {
  const raw = typeof answer === 'string' ? answer.trim() : '';
  return raw.toLowerCase().startsWith('n') ? 'No' : 'Yes';
};

const ADVANCED_TYPE_OPTIONS = [
  {
    id: ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED,
    label: ADVANCED_INTERVIEW_TYPE_LABELS[ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED],
    description:
      'Create custom question banks split across competencies (e.g. Sales, Leadership). The AI randomly picks N questions from each module per candidate.',
    icon: ClipboardDocumentListIcon,
    enabled: true,
  },
  {
    id: ADVANCED_INTERVIEW_TYPES.READING_ASSESSMENT,
    label: ADVANCED_INTERVIEW_TYPE_LABELS[ADVANCED_INTERVIEW_TYPES.READING_ASSESSMENT],
    description:
      'Evaluate English comprehension, pronunciation and communication skills by having candidates read passages aloud.',
    icon: DocumentTextIcon,
    enabled: false,
  },
  {
    id: ADVANCED_INTERVIEW_TYPES.CODE_INTERVIEW,
    label: ADVANCED_INTERVIEW_TYPE_LABELS[ADVANCED_INTERVIEW_TYPES.CODE_INTERVIEW],
    description:
      'Live coding with AI discussion. Candidate writes code while the AI reviews and asks questions.',
    icon: CodeBracketIcon,
    enabled: false,
  },
  {
    id: ADVANCED_INTERVIEW_TYPES.CASE_STUDY,
    label: ADVANCED_INTERVIEW_TYPE_LABELS[ADVANCED_INTERVIEW_TYPES.CASE_STUDY],
    description:
      'Present a business scenario and evaluate analytical reasoning and problem-solving skills.',
    icon: BriefcaseIcon,
    enabled: false,
  },
  {
    id: ADVANCED_INTERVIEW_TYPES.RESUME_DEEP_DIVE,
    label: ADVANCED_INTERVIEW_TYPE_LABELS[ADVANCED_INTERVIEW_TYPES.RESUME_DEEP_DIVE],
    description:
      "AI-driven interview deeply tailored to the candidate's resume and experience.",
    icon: DocumentMagnifyingGlassIcon,
    enabled: false,
  },
  {
    id: ADVANCED_INTERVIEW_TYPES.AVATAR,
    label: ADVANCED_INTERVIEW_TYPE_LABELS[ADVANCED_INTERVIEW_TYPES.AVATAR],
    description:
      'Interview with a lifelike AI avatar for a more immersive candidate experience.',
    icon: UserCircleIcon,
    enabled: false,
  },
];

// Pre-filled AI Intro. Stored in English for every language, Hindi and Hinglish
// included: the agent recognises this default and speaks its own greeting in
// the interview language (for hi / hi-en: the Hindi self-introduction followed
// by the Hindi-or-English question), and translates a custom English intro. See
// livekit-agent/src/main.js (isDefaultEnglishIntro) and languages/hi-en.js.
const STANDARD_AI_INTRO_DEFAULT =
  'Welcome, I am your AI Interviewer. This interview is designed to evaluate your qualifications for this position. The session is monitored. Let me know when you are ready.';
// Legacy pre-fill for Hindi/Hinglish, kept only so an unmodified old value is
// recognised and swapped back to the English default.
const HINGLISH_AI_INTRO_DEFAULT = 'नमस्ते, मैं आपका AI interviewer हूँ।';

// Interview language options shown in the standard modal's language picker.
// English is the first option and the default; the rest are regional languages.
// Spoken language is driven entirely by `config.language` (this id) — see the
// agent's getLanguageModule(config.language). Single source of truth for both
// the dropdown and submit validation.
const LANGUAGE_OPTIONS = [
  { id: 'en', name: 'English', desc: 'Interviewer speaks in English', icon: 'En' },
  { id: 'hi', name: 'Hindi', desc: 'Interviewer speaks in Hindi', icon: 'हि' },
  { id: 'hi-en', name: 'Hinglish', desc: 'English questions, Hindi answers OK', icon: 'हि' },
  { id: 'en-hi', name: 'Hinglish 2', desc: 'Speaks English only; Hindi answers OK', icon: 'हि' },
  { id: 'kn', name: 'Kannada', desc: 'Interviewer speaks in Kannada', icon: 'ಕ' },
  { id: 'ta', name: 'Tamil', desc: 'Interviewer speaks in Tamil', icon: 'த' },
  { id: 'bn', name: 'Bengali', desc: 'Interviewer speaks in Bengali', icon: 'ব' },
  { id: 'te', name: 'Telugu', desc: 'Interviewer speaks in Telugu', icon: 'తె' },
  { id: 'mr', name: 'Marathi', desc: 'Interviewer speaks in Marathi', icon: 'म' },
  { id: 'gu', name: 'Gujarati', desc: 'Interviewer speaks in Gujarati', icon: 'ગ' },
  { id: 'ml', name: 'Malayalam', desc: 'Interviewer speaks in Malayalam', icon: 'മ' },
  { id: 'pa', name: 'Punjabi', desc: 'Interviewer speaks in Punjabi', icon: 'ਪ' },
  { id: 'tl', name: 'Taglish', desc: 'English by default, switches to Tagalog if needed', icon: 'Tl' },
  { id: 'fr', name: 'French', desc: 'Interviewer speaks in French', icon: 'Fr' },
];
const SUPPORTED_LANGUAGE_CODES = LANGUAGE_OPTIONS.map((l) => l.id);

// Derive the saved advancedType/typeConfig from the chosen language.
// - English with no advanced type => plain standard interview (advancedType null).
// - Any non-English language with no advanced type => tag 'multi-language' so
//   analytics/library filters still recognize it (data-shape parity with
//   interviews created the old way).
// - An explicit advanced type always wins; the language just rides along in
//   config.language / typeConfig.language.
const deriveAdvancedTypeForLanguage = (advancedType, languageCode) => {
  let effectiveAdvancedType = advancedType;
  if (effectiveAdvancedType === ADVANCED_INTERVIEW_TYPES.MULTI_LANGUAGE && languageCode === 'en') {
    effectiveAdvancedType = null;
  } else if (!effectiveAdvancedType && languageCode !== 'en') {
    effectiveAdvancedType = ADVANCED_INTERVIEW_TYPES.MULTI_LANGUAGE;
  }
  return {
    advancedType: effectiveAdvancedType,
    typeConfig: effectiveAdvancedType ? { language: languageCode } : null,
  };
};

// Cartesia voice IDs grouped by language. Hoisted to module scope so the
// language-change effect (which snaps voiceId to the selected language's
// default) and the voice picker share one source of truth.
const englishVoiceOptions = [
  {
    id: '7ea5e9c2-b719-4dc3-b870-5ba5f14d31d8',
    name: 'Janvi',
    gender: 'Janvi',
    accent: 'Indian Female',
    label: 'Indian Female',
    tone: 'Steady Agent • Warm & Professional',
    details: 'Steady Agent',
    preview: '/voices/female-indian.mp3',
  },
  {
    id: '638efaaa-4d0c-442e-b701-3fae16aad012',
    name: 'Sameer',
    gender: 'Sameer',
    accent: 'Indian Male',
    label: 'Indian Male',
    tone: 'Problem Solver • Friendly & Solution-Oriented',
    details: 'Problem Solver',
    preview: '/voices/sameer.mp3',
  },
];
// Native Hindi voices (Cartesia Sonic-3 via LiveKit Inference).
// Used when the interview language is set to Hindi or Hinglish.
// First entry is the default. Rohan was retired — see lib/replacedVoices.js.
const hindiVoiceOptions = [
  {
    id: '0f14d8cb-f039-41fe-a813-a9b4bee7eed8',
    name: 'Nisha',
    gender: 'Nisha',
    accent: 'Hindi Female',
    label: 'Hindi Female',
    tone: 'Steady & Reassuring',
    details: 'Steady & Reassuring',
    preview: '/voices_hindi/Nisha.wav',
  },
  {
    id: '098fb15d-2597-4186-8b74-25340050b6e7',
    name: 'Vishal',
    gender: 'Vishal',
    accent: 'Hindi Male',
    label: 'Hindi Male',
    tone: 'Assured & Professional',
    details: 'Assured & Professional',
    preview: '/voices_hindi/Vishal.wav',
  },
];
// Regional language voice options
const kannadaVoiceOptions = [
  {
    id: '6baae46d-1226-45b5-a976-c7f9b797aae2',
    name: 'Prakash',
    gender: 'Prakash',
    accent: 'Kannada Male',
    label: 'Kannada Male',
    tone: 'Firm & Articulate',
    details: 'Firm & Articulate',
    preview: '/voices_kannada/prakash_kannada.wav',
  },
  {
    id: '7c6219d2-e8d2-462c-89d8-7ecba7c75d65',
    name: 'Divya',
    gender: 'Divya',
    accent: 'Kannada Female',
    label: 'Kannada Female',
    tone: 'Lively & Cheerful',
    details: 'Lively & Cheerful',
    preview: '/voices_kannada/divya_kannada.wav',
  },
];
const tamilVoiceOptions = [
  {
    id: 'd2870b91-1b4c-47ab-81a8-3718d8e9c222',
    name: 'Arun',
    gender: 'Arun',
    accent: 'Tamil Male',
    label: 'Tamil Male',
    tone: 'Expressive & Storytelling',
    details: 'Expressive & Storytelling',
    preview: '/voices_tamil/arun_tamil.wav',
  },
  {
    id: '7f98e662-142d-41ba-89a2-12452640ce6d',
    name: 'Lakshmi',
    gender: 'Lakshmi',
    accent: 'Tamil Female',
    label: 'Tamil Female',
    tone: 'Casual & Upbeat',
    details: 'Casual & Upbeat',
    preview: '/voices_tamil/lakshmi_tamil.wav',
  },
];
const bengaliVoiceOptions = [
  {
    id: '2ba861ea-7cdc-43d1-8608-4045b5a41de5',
    name: 'Rubel',
    gender: 'Rubel',
    accent: 'Bengali Male',
    label: 'Bengali Male',
    tone: 'Casual & Everyday',
    details: 'Casual & Everyday',
    preview: '/voices_bengali/Rubel_bengali.wav',
  },
];
const teluguVoiceOptions = [
  {
    id: 'cf061d8b-a752-4865-81a2-57570a6e0565',
    name: 'Ramya',
    gender: 'Ramya',
    accent: 'Telugu Female',
    label: 'Telugu Female',
    tone: 'Warm & Welcoming',
    details: 'Warm & Welcoming',
    preview: '/voices_telugu/ramya_telugu.wav',
  },
];
const marathiVoiceOptions = [
  {
    id: 'f227bc18-3704-47fe-b759-8c78a450fdfa',
    name: 'Suresh',
    gender: 'Suresh',
    accent: 'Marathi Male',
    label: 'Marathi Male',
    tone: 'Warm & Conversational',
    details: 'Warm & Conversational',
    preview: '/voices_marathi/suresh_marathi.wav',
  },
];
const gujaratiVoiceOptions = [
  {
    id: '91925fe5-42ee-4ebe-96c1-c84b12a85a32',
    name: 'Amit',
    gender: 'Amit',
    accent: 'Gujarati Male',
    label: 'Gujarati Male',
    tone: 'Clear & Friendly',
    details: 'Clear & Friendly',
    preview: '/voices_gujrathi/amit_gujarathi.wav',
  },
];
const malayalamVoiceOptions = [
  {
    id: '374b80da-e622-4dfc-90f6-1eeb13d331c9',
    name: 'Vijay',
    gender: 'Vijay',
    accent: 'Malayalam Male',
    label: 'Malayalam Male',
    tone: 'Clear & Conversational',
    details: 'Clear & Conversational',
    preview: '/voices_malayalam/vijay_malayalam.wav',
  },
];
// Punjabi voice ID must stay in sync with livekit-agent/src/constants/voices.js.
const punjabiVoiceOptions = [
  {
    id: '8bacd442-a107-4ec1-b6f1-2fcb3f6f4d56',
    name: 'Gurpreet',
    gender: 'Gurpreet',
    accent: 'Punjabi Male',
    label: 'Punjabi Male',
    tone: 'Clear & Conversational',
    details: 'Clear & Conversational',
    preview: '/voices_punjabi/gurpreet_punjabi.wav',
  },
];
// Tagalog voice ID must stay in sync with livekit-agent/src/constants/voices.js.
const tagalogVoiceOptions = [
  {
    id: '42332792-6e9f-4b2c-a106-3ff97e34a79d',
    name: 'Jerome',
    gender: 'Jerome',
    accent: 'Filipino Male',
    label: 'Filipino Male',
    tone: 'Clear & Conversational',
    details: 'Clear & Conversational',
    preview: '/voices_tagalog/jerome_tagalog.wav',
  },
  {
    id: 'd1c819ba-0384-496b-b63b-eb57a96a43cc',
    name: 'Angel',
    gender: 'Angel',
    accent: 'Filipino Female',
    label: 'Filipino Female',
    tone: 'Warm & Welcoming',
    details: 'Warm & Welcoming',
    preview: '/voices_tagalog/angel_tagalog.wav',
  },
];
// French voice ID must stay in sync with livekit-agent/src/constants/voices.js.
const frenchVoiceOptions = [
  {
    id: '996ec149-0dca-4389-ad08-e2d6f906b4bf',
    name: 'Mathis',
    gender: 'Mathis',
    accent: 'French Male',
    label: 'French Male',
    tone: 'Clear & Conversational',
    details: 'Clear & Conversational',
    preview: '/voices_french/mathis_french.wav',
  },
];
const VOICE_OPTIONS_BY_LANGUAGE = {
  en: englishVoiceOptions,
  hi: hindiVoiceOptions,
  'hi-en': hindiVoiceOptions,
  'en-hi': hindiVoiceOptions,
  kn: kannadaVoiceOptions,
  ta: tamilVoiceOptions,
  bn: bengaliVoiceOptions,
  te: teluguVoiceOptions,
  mr: marathiVoiceOptions,
  gu: gujaratiVoiceOptions,
  ml: malayalamVoiceOptions,
  pa: punjabiVoiceOptions,
  tl: tagalogVoiceOptions,
  fr: frenchVoiceOptions,
};

const InterviewCategoryStep = ({ onSelectCategory }) => (
  <div className="flex flex-col items-center justify-center py-8 px-4 h-full">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl w-full">
      <button
        type="button"
        data-testid="standard-interview-btn"
        onClick={() => onSelectCategory('standard')}
        className="group relative p-6 bg-white border-2 border-gray-200 rounded-2xl hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 text-left h-full"
      >
        <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-[#007BFF]/10 to-[#007BFF]/5 mb-4 group-hover:from-[#007BFF]/20 group-hover:to-[#007BFF]/10 transition-colors">
          <MicrophoneIcon className="w-7 h-7 text-[#007BFF]" />
        </div>
        <h4 className="text-lg font-semibold text-gray-900 mb-2">Standard Interview</h4>
        <p className="text-sm text-gray-600 mb-4">
          AI-powered voice or video interview. The AI asks questions and the candidate answers
          conversationally.
        </p>
        <ul className="space-y-2.5 text-sm text-gray-500">
          <li className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span>AI-generated or custom questions</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span>Voice or video interview mode</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span>Automated scoring &amp; analysis</span>
          </li>
        </ul>
      </button>

      <button
        type="button"
        data-testid="advanced-interview-btn"
        onClick={() => onSelectCategory('advanced')}
        className="group relative p-6 bg-white border-2 border-gray-200 rounded-2xl hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 text-left h-full"
      >
        <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-purple-100 mb-4 group-hover:bg-purple-200 transition-colors">
          <SparklesIcon className="w-7 h-7 text-purple-600" />
        </div>
        <h4 className="text-lg font-semibold text-gray-900 mb-2">Advanced Interview</h4>
        <p className="text-sm text-gray-600 mb-4">
          Specialized interview formats for deeper skill evaluation — reading assessments, code
          interviews, case studies &amp; more.
        </p>
        <ul className="space-y-2.5 text-sm text-gray-500">
          <li className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span>Reading &amp; pronunciation assessment</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span>Code interviews &amp; case studies</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span>Domain-specific skill evaluation</span>
          </li>
        </ul>
      </button>
    </div>
  </div>
);

const AdvancedTypePickerStep = ({ onSelectAdvancedType }) => (
  <div className="flex flex-col items-center py-6 px-4">
    <div className="w-full max-w-4xl">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ADVANCED_TYPE_OPTIONS.map((option) => {
          const Icon = option.icon;
          return (
            <button
              key={option.id}
              type="button"
              disabled={!option.enabled}
              onClick={() => option.enabled && onSelectAdvancedType(option.id)}
              className={`relative p-5 rounded-xl border-2 text-left transition-all duration-200 ${option.enabled
                  ? 'bg-white border-gray-200 hover:border-purple-400 hover:shadow-md cursor-pointer'
                  : 'bg-gray-50 border-gray-100 cursor-not-allowed opacity-70'
                }`}
            >
              {!option.enabled && (
                <span className="absolute top-3 right-3 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-500">
                  Coming soon
                </span>
              )}
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-lg mb-3 ${option.enabled ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-400'
                  }`}
              >
                <Icon className="w-5 h-5" />
              </div>
              <h4
                className={`text-sm font-semibold mb-1 ${option.enabled ? 'text-gray-900' : 'text-gray-500'
                  }`}
              >
                {option.label}
              </h4>
              <p
                className={`text-xs leading-relaxed ${option.enabled ? 'text-gray-500' : 'text-gray-400'
                  }`}
              >
                {option.description}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  </div>
);

const CreationModeStep = ({ onSelectMode }) => (
  <div className="flex flex-col items-center justify-center py-8 px-4 h-full">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl w-full">
      <button
        type="button"
        data-testid="create-with-ai-btn"
        onClick={() => onSelectMode(CREATION_MODE.AI)}
        className="group relative p-6 bg-white border-2 border-gray-200 rounded-2xl hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 text-left h-full"
      >
        <div className="absolute top-4 right-4">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-[#007BFF] text-white">
            Recommended
          </span>
        </div>
        <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-[#007BFF]/10 to-[#007BFF]/5 mb-4 group-hover:from-[#007BFF]/20 group-hover:to-[#007BFF]/10 transition-colors">
          <SparklesIcon className="w-7 h-7 text-[#007BFF]" />
        </div>
        <h4 className="text-lg font-semibold text-gray-900 mb-2">Create with AI</h4>
        <p className="text-sm text-gray-600 mb-4">
          Let AI generate interview questions from your context, role, and selected categories.
        </p>
        <ul className="space-y-2.5 text-sm text-gray-500">
          <li className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span>Fast first draft of questions</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span>Role and category aligned prompts</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span>Edit and refine before publishing</span>
          </li>
        </ul>
      </button>

      <button
        type="button"
        data-testid="create-manually-btn"
        onClick={() => onSelectMode(CREATION_MODE.MANUAL)}
        className="group relative p-6 bg-white border-2 border-gray-200 rounded-2xl hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 text-left h-full"
      >
        <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-gray-100 mb-4 group-hover:bg-[#007BFF]/10 transition-colors">
          <PlusIcon className="w-7 h-7 text-gray-600 group-hover:text-[#007BFF]" />
        </div>
        <h4 className="text-lg font-semibold text-gray-900 mb-2">Create from Question Bank</h4>
        <p className="text-sm text-gray-600 mb-4">
          Upload your question file and let us instantly convert it into a structured interview flow.
        </p>
        <ul className="space-y-2.5 text-sm text-gray-500">
          <li className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span>Bring your own curated question bank</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span>Preserves question order and intent</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span>Perfect for standardized interview packs</span>
          </li>
        </ul>
      </button>
    </div>
  </div>
);

const AIInterviewCreate = ({
  onClose,
  isModal = false,
  jobPostingId: propJobPostingId,
  returnTo: propReturnTo,
  skipInviteStep = false,
  screeningName = '',
  enableCreationModeSelection = false,
  enableCreatorSelection = false,
  enableTypeSelection = false,
  hideExternalCreationLink = false,
  forceShowVisibilityStep = false,
  // When set, open directly at "Interview Flow Edit" step for an existing AI Interview library template.
  // Loads the template from the library and saves changes back via PUT /api/library/ai-interviews/:id.
  // Screening interviews linked to this template are updated too (see
  // server/modules/assessment/library/aiInterviewLibrarySync.js); candidates
  // already invited keep the version frozen in their session.
  editLibraryInterviewId = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location && location.state ? location.state : {};
  const extractId = (val) => (val && typeof val === 'object' ? val._id : val);
  const linkedJobPostingId = extractId(propJobPostingId) || extractId(locationState.jobPostingId) || '';
  const returnTo = propReturnTo || locationState.returnTo || '/dashboard/ai-interviews';
  const fromHireLoop = locationState.fromHireLoop || false;
  const fromJobResults = locationState.fromJobResults || false;
  const isLibraryCreateFlow = isModal && !linkedJobPostingId && !editLibraryInterviewId;
  const isEditLibrary = Boolean(editLibraryInterviewId);
  // Always use LiveKit - batch mode is deprecated
  const initialDeliveryMode = 'livekit';

  const { user } = useAuth();
  const MAX_AI_INTERVIEW_GENERATED_QUESTIONS = user?.limits?.max_questions_per_interview || 30;
  const MAX_INTERVIEW_QUESTIONS_TOTAL = user?.limits?.max_questions_per_interview || 30;

  // Question category tags
  const questionTags = useMemo(
    () => [
      {
        id: 'eligibility',
        label: 'Eligibility',
        color: 'yellow',
        description: 'Location, CTC, Notice Period',
      },
      { id: 'technical', label: 'Technical', color: 'blue', description: 'Skills & Experience' },
      {
        id: 'behavioral',
        label: 'Behavioral',
        color: 'green',
        description: 'Soft Skills & Past Behavior',
      },
      { id: 'cultural', label: 'Cultural', color: 'purple', description: 'Values & Work Style' },
    ],
    []
  );

  const interviewCategoryOptions = useMemo(
    () => [
      { value: 'Sales', label: 'Sales', description: 'For SDRs, AEs, and revenue roles.' },
      { value: 'Marketing', label: 'Marketing', description: 'For growth, content, and brand roles.' },
      { value: 'Software Development', label: 'Software Engineering', description: 'For backend, frontend, and full-stack roles.' },
      { value: 'Data & Analytics', label: 'Data & Analytics', description: 'For data analysts, scientists, and BI roles.' },
      { value: 'Product Management', label: 'Product Management', description: 'For PMs and product strategy roles.' },
      { value: 'Talent & Recruiting', label: 'Talent & Recruiting', description: 'For recruiters and talent partners.' },
      { value: 'Customer Success', label: 'Customer Success', description: 'For CS, onboarding, and account roles.' },
      { value: 'Operations', label: 'Operations', description: 'For business, ops, and enablement roles.' },
      { value: 'Finance', label: 'Finance', description: 'For FP&A, accounting, and finance roles.' },
      { value: 'People & HR', label: 'People & HR', description: 'For HRBPs and people operations roles.' },
      { value: 'IT', label: 'IT', description: 'For IT support, systems, and infrastructure roles.' },
      { value: 'Engineering', label: 'Engineering', description: 'For engineering roles across disciplines.' },
      { value: 'General', label: 'General', description: 'A broad evaluation for mixed roles.' },
      { value: 'Communication', label: 'Communication', description: 'For roles requiring strong verbal and written communication skills.' },
      { value: 'Customer Support', label: 'Customer Support', description: 'For technical support, customer service, and helpdesk.' },
      { value: 'Design & UX', label: 'Design & UX', description: 'For UX/UI designers, graphic designers, and researchers.' },
      { value: 'Accounting', label: 'Accounting', description: 'For accountants, bookkeepers, and tax professionals.' },
      { value: 'Legal', label: 'Legal', description: 'For legal counsel, paralegals, and compliance officers.' },
      { value: 'Healthcare', label: 'Healthcare', description: 'For nurses, medical assistants, and healthcare administrators.' },
      { value: 'Education & Training', label: 'Education & Training', description: 'For teachers, trainers, and instructional designers.' },
      { value: 'Administration', label: 'Administration', description: 'For administrative assistants, office managers, and receptionists.' },
      { value: 'Project Management', label: 'Project Management', description: 'For Scrum masters, Agile coaches, and project coordinators.' },
      { value: 'Business Development', label: 'Business Development', description: 'For partnerships, alliances, and B2B growth roles.' },
      { value: 'Supply Chain & Logistics', label: 'Supply Chain & Logistics', description: 'For procurement, inventory, and logistics managers.' },
      { value: 'Quality Assurance', label: 'Quality Assurance', description: 'For manual/automation testers and QA engineers.' },
      { value: 'Cybersecurity', label: 'Cybersecurity', description: 'For security analysts, penetration testers, and compliance.' },
    ],
    []
  );

  const [formData, setFormData] = useState({
    title: '',
    category: '',
    selectedTags: [],
    context: '',
    questions: [],
    type: 'video',
    voiceId: '7ea5e9c2-b719-4dc3-b870-5ba5f14d31d8',
    language: 'english',
    personality: 'professional',
    maxQuestions: 6, // Number of questions to generate
    jobPostingId: linkedJobPostingId,
    deliveryMode: initialDeliveryMode,
    // Eligibility - auto-parsed from job posting, candidate provides notice period during interview
    eligibility: {
      enabled: false, // Will auto-enable when eligibility tag selected
    },
    // AI Intro/Outro messages
    aiIntro: STANDARD_AI_INTRO_DEFAULT,
    aiOutro:
      'Thank you for completing this interview. Your responses have been recorded and will be reviewed by the team. We appreciate your time and will be in contact regarding next steps.',
    // Invite settings
    inviteExpiry: '1-week', // Default expiry
    emailReminderFrequency: 'none', // Email reminder frequency
    activeSharePlatform: 'email', // Active share platform
    // Proctoring settings
    proctoring: {
      browserActivityMonitoring: true,
      responseDelayTracking: true,
      speechPatternAnalysis: true,
      transcriptAnalysis: true,
      multiSpeakerDetection: true,
      voiceConsistencyCheck: true,
      randomInterruptQuestions: true,
      // Video proctoring (only applies when type === 'video')
      facePresenceDetection: true,
      multipleFaceDetection: true,
      faceIdentityConsistency: true,
    },
  });

  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [voiceDropdownOpen, setVoiceDropdownOpen] = useState(false);
  const voiceDropdownRef = useRef(null);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef(null);
  const [interviewModeDropdownOpen, setInterviewModeDropdownOpen] = useState(false);
  const interviewModeDropdownRef = useRef(null);
  const audioPreviewRef = useRef(null);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const categoryDropdownRef = useRef(null);

  // JD upload state for AI mode without linked job posting (library flow)
  const [jdFile, setJdFile] = useState(null);
  const [jdProcessing, setJdProcessing] = useState(false);
  const [jdExtractedSummary, setJdExtractedSummary] = useState(null);
  const [jdExtractedJobData, setJdExtractedJobData] = useState(null);
  const [isDraggingJd, setIsDraggingJd] = useState(false);
  const [showJdUploadModal, setShowJdUploadModal] = useState(false);
  const jdFileInputRef = useRef(null);
  const [questionFile, setQuestionFile] = useState(null);
  const [isDraggingQuestionFile, setIsDraggingQuestionFile] = useState(false);
  const questionFileInputRef = useRef(null);

  const handleVoicePreview = useCallback(
    (id, preview) => {
      try {
        if (audioPreviewRef.current) {
          audioPreviewRef.current.pause();
          audioPreviewRef.current.currentTime = 0;
          audioPreviewRef.current = null;
        }

        if (playingVoiceId === id) {
          setPlayingVoiceId(null);
          return;
        }

        const audio = new Audio(preview);
        audioPreviewRef.current = audio;
        setPlayingVoiceId(id);

        audio.play().catch(() => {
          setPlayingVoiceId((current) => (current === id ? null : current));
          audioPreviewRef.current = null;
        });

        audio.onended = () => {
          setPlayingVoiceId((current) => (current === id ? null : current));
          audioPreviewRef.current = null;
        };
      } catch (err) {
        setPlayingVoiceId((current) => (current === id ? null : current));
        audioPreviewRef.current = null;
      }
    },
    [playingVoiceId]
  );

  // Close voice dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (voiceDropdownRef.current && !voiceDropdownRef.current.contains(event.target)) {
        setVoiceDropdownOpen(false);
      }
    };
    if (voiceDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [voiceDropdownOpen]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
    };
    if (langDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langDropdownOpen]);

  // Close interview mode dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        interviewModeDropdownRef.current &&
        !interviewModeDropdownRef.current.contains(event.target)
      ) {
        setInterviewModeDropdownOpen(false);
      }
    };
    if (interviewModeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [interviewModeDropdownOpen]);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryOpen(false);
      }
    };
    if (isCategoryOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCategoryOpen]);

  const [loading, setLoading] = useState(false);

  // State to store the created interview ID
  const [interviewId, setInterviewId] = useState(null);

  // Edit library interview mode state
  const [editLibraryLoading, setEditLibraryLoading] = useState(Boolean(editLibraryInterviewId));
  const [editLibrarySaving, setEditLibrarySaving] = useState(false);

  // Step management (3 steps now: Details -> Preview & Edit -> Invite)
  const [currentStep, setCurrentStep] = useState(
    isEditLibrary
      ? 2
      : enableCreatorSelection
        ? -1
        : forceShowVisibilityStep
          ? -2
          : enableTypeSelection || enableCreationModeSelection
            ? 0
            : 1
  );
  const [creatorChoice, setCreatorChoice] = useState(null);
  const [creationMode, setCreationMode] = useState(
    enableCreationModeSelection ? null : CREATION_MODE.AI
  );
  const isManualLibraryFlow = creationMode === CREATION_MODE.MANUAL && isLibraryCreateFlow;
  // Interview type selection (null = not picked yet, 'standard' | 'advanced')
  const [interviewCategory, setInterviewCategory] = useState(
    enableTypeSelection ? null : 'standard'
  );
  // Advanced sub-type (null for standard interviews)
  const [advancedType, setAdvancedType] = useState(null);
  // Modules for rubric-based interviews
  const [rubricModules, setRubricModules] = useState([]);
  // True when an advanced type that supports method selection (e.g. Multi-Language)
  // has been picked but the user hasn't chosen AI vs Question Bank yet.
  const [advancedMethodPending, setAdvancedMethodPending] = useState(false);
  // Which advanced types offer the Choose Method step. (None currently — the
  // remaining advanced types are all "Coming soon"; language now lives in the
  // standard Details step rather than a Multi-Language advanced type.)
  const ADVANCED_TYPES_WITH_METHOD_CHOICE = new Set([]);
  // Language code for the interview ('en' default, 'hi' for Hindi, 'hi-en' for Hinglish).
  // Drives candidate-side STT/TTS/agent prompt; UI display (translation toggle)
  // is handled separately. Standard interviews always stay 'en'.
  const [interviewLanguageCode, setInterviewLanguageCode] = useState('en');
  // When the interview language changes, snap the saved voiceId to that
  // language's default whenever the current voice doesn't belong to it. Keeps
  // the voice list and the saved voice following the language automatically.
  useEffect(() => {
    setFormData((prev) => {
      const options = VOICE_OPTIONS_BY_LANGUAGE[interviewLanguageCode] || englishVoiceOptions;
      const stillValid = options.some((v) => v.id === prev.voiceId);
      return stillValid ? prev : { ...prev, voiceId: options[0].id };
    });
  }, [interviewLanguageCode]);
  // Keep the legacy `formData.language` field in sync with the canonical
  // language code so the two cannot drift on save/edit cycles.
  useEffect(() => {
    setFormData((prev) =>
      prev.language === interviewLanguageCode
        ? prev
        : { ...prev, language: interviewLanguageCode }
    );
  }, [interviewLanguageCode]);
  // A new interview whose intro is still the old Hindi pre-fill gets the English
  // default instead. Only while creating: an existing interview's saved intro is
  // left exactly as it is.
  useEffect(() => {
    if (isEditLibrary) return;
    setFormData((prev) => {
      const current = prev.aiIntro;
      const isHinglishDefault =
        current === HINGLISH_AI_INTRO_DEFAULT || current === 'Namaste, mein aapka AI interviewer hoon.';
      return isHinglishDefault ? { ...prev, aiIntro: STANDARD_AI_INTRO_DEFAULT } : prev;
    });
  }, [interviewLanguageCode, isEditLibrary]);
  // Step 0 has two phases when enableTypeSelection is on: 'type' -> 'method'
  // When a category is picked, phase flips to 'method'. Until then it's 'type'.
  const step0Phase = enableTypeSelection
    ? interviewCategory
      ? 'method'
      : 'type'
    : 'method';
  const [isAutoGenerating, setIsAutoGenerating] = useState(false);

  // Visibility step state
  const [visibilityLevel, setVisibilityLevel] = useState(null);
  const [selectedSharedDepartmentIds, setSelectedSharedDepartmentIds] = useState(new Set());
  const [accessOptions, setAccessOptions] = useState(null);
  const [loadingAccessOptions, setLoadingAccessOptions] = useState(false);
  const showVisibilityStep = (enableCreatorSelection && isLibraryCreateFlow) || forceShowVisibilityStep;

  // Fetch access options (departments) for visibility step
  useEffect(() => {
    if (!showVisibilityStep) return;
    let cancelled = false;
    const fetchOptions = async () => {
      setLoadingAccessOptions(true);
      try {
        const res = await axios.get('/api/library/access-options');
        if (cancelled) return;
        setAccessOptions(res.data);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch access options:', err);
        }
      } finally {
        if (!cancelled) {
          setLoadingAccessOptions(false);
        }
      }
    };
    fetchOptions();
    return () => {
      cancelled = true;
    };
  }, [showVisibilityStep]);

  const toggleSharedDepartment = useCallback((deptId) => {
    setSelectedSharedDepartmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(deptId)) next.delete(deptId);
      else next.add(deptId);
      return next;
    });
  }, []);

  // AI Recommendations state (keeping for potential future use)
  const [_aiRecommendations, setAiRecommendations] = useState(null);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [jobPostingDetails, setJobPostingDetails] = useState(null);

  // Question generation animation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingQuestions, setGeneratingQuestions] = useState([]);
  const [expandedQuestionIndex, setExpandedQuestionIndex] = useState(null);
  // Tracks the "new question placeholder" created by "Add Question".
  // While one placeholder is unsaved, we block adding more questions to avoid confusing the user.
  const [pendingQuestionLocalId, setPendingQuestionLocalId] = useState(null);
  const pendingQuestionLockRef = useRef(false);
  /** Prevents double-submit on "Generate questions" before React re-renders (duplicate API calls). */
  const interviewSetupAdvanceLockRef = useRef(false);
  const [expandedIntro, setExpandedIntro] = useState(false);
  const [expandedOutro, setExpandedOutro] = useState(false);
  const questionEditSnapshotsRef = useRef({});
  const [autoGeneratingIndices, setAutoGeneratingIndices] = useState(new Set());
  // Overwrite-confirmation modal state for "Generate" expected answer
  const [overwriteExpectedConfirm, setOverwriteExpectedConfirm] = useState({
    open: false,
    index: null,
  });

  // Coming Soon modal state
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [comingSoonFeature, setComingSoonFeature] = useState('');

  // Demo mode state for interactive preview
  const [demoMode, setDemoMode] = useState({
    active: false,
    currentQuestionIndex: -1, // -1 = intro, questions.length = outro
    isPlaying: false,
  });
  const [hasSavedToLibrary, setHasSavedToLibrary] = useState(false);
  const [savingToLibrary, setSavingToLibrary] = useState(false);
  const [showSaveToLibraryConfirm, setShowSaveToLibraryConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [externalCreationLink, setExternalCreationLink] = useState('');
  const [externalLinkCopied, setExternalLinkCopied] = useState(false);

  const handleGenerateExternalCreationLink = useCallback(async () => {
    try {
      const res = await axios.post('/api/ai-interviews/external-create-links');
      const code = res.data?.code;
      if (!code) throw new Error('No code returned');
      const link = `${window.location.origin}/external/ai-interview-create/${code}`;
      setExternalCreationLink(link);

      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setExternalLinkCopied(true);
        setTimeout(() => setExternalLinkCopied(false), 2000);
        toast.success('External interview link copied.');
      } else {
        toast.success('External interview link generated.');
      }
    } catch (err) {
      console.error('External interview link error:', err);
      toast.error('Failed to generate external link.');
    }
  }, []);

  // Handle step navigation with validation (3 steps)
  // `fromGenerateCta`: primary "Generate questions" on Details — always re-run AI when already have questions.
  const handleStepNavigation = async (targetStep, navOptions = {}) => {
    const fromGenerateCta = Boolean(navOptions.fromGenerateCta);
    // Always allow going back to previous steps
    if (targetStep < currentStep) {
      setCurrentStep(targetStep);
      return;
    }

    // Validate forward navigation
    if (targetStep === 2) {
      // Step 2: Edit Questions
      const effectiveTitle =
        formData.title ||
        (jobPostingDetails?.title ? `${jobPostingDetails.title} AI Interview` : '');
      if (!effectiveTitle.trim()) {
        toast.error('Loading job details... Please wait a moment.');
        return;
      }
      if (!formData.category) {
        toast.error('Please select a category.');
        return;
      }
      if (
        (!isLibraryCreateFlow || creationMode !== CREATION_MODE.MANUAL) &&
        advancedType !== ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED &&
        formData.selectedTags.length === 0
      ) {
        toast.error('Please select at least one question category.');
        return;
      }
      if (isContextRequired && !contextValue) {
        toast.error('Please add Context to continue.');
        return;
      }
      // Language must be one of the supported codes (English + regional). English
      // is the default, so there's no hard requirement to pick a non-English one.
      if (!SUPPORTED_LANGUAGE_CODES.includes(interviewLanguageCode)) {
        toast.error('Please select a valid interview language.');
        return;
      }
      const requiresManualQuestionFile =
        isLibraryCreateFlow && creationMode === CREATION_MODE.MANUAL;
      if (requiresManualQuestionFile && !questionFile) {
        toast.error('Upload a Question Bank file to continue.');
        return;
      }

      if (interviewSetupAdvanceLockRef.current) {
        return;
      }
      interviewSetupAdvanceLockRef.current = true;

      try {
        // Create interview first to get interviewId (except for library create flow, where we can
        // generate questions before persisting the interview)
        if (!interviewId && !isLibraryCreateFlow) {
          try {
            setLoading(true);
            await handleCreateInterviewForStep();
          } catch (error) {
            console.error('Error creating interview:', error);
            return;
          } finally {
            setLoading(false);
          }
        }

        // AI mode: run generator when there are no questions yet, or when user explicitly clicks
        // "Generate questions" (not when only using the sidebar to open Edit — avoids wiping edits).
        const shouldAutoGenerate = creationMode !== CREATION_MODE.MANUAL;
        const shouldRunAi =
          shouldAutoGenerate &&
          (fromGenerateCta || formData.questions.length === 0);
        const shouldProcessManualQuestionFile =
          creationMode === CREATION_MODE.MANUAL &&
          isLibraryCreateFlow &&
          Boolean(questionFile) &&
          (fromGenerateCta || formData.questions.length === 0);

        if (advancedType === ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED) {
          setCurrentStep(2);
        } else if (shouldRunAi || shouldProcessManualQuestionFile) {
          setIsAutoGenerating(true);
          try {
            await handleGetAIRecommendations();
            setCurrentStep(2);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error generating questions:', error);
            toast.error('Failed to generate questions. Please try again.');
          } finally {
            setIsAutoGenerating(false);
          }
        } else {
          setCurrentStep(2);
        }
      } finally {
        interviewSetupAdvanceLockRef.current = false;
      }
    }
    
    if (targetStep === 3) {
      // Step 3: Proctoring - Save proctoring config before proceeding
      if (advancedType === ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED) {
        if (rubricModules.length === 0) {
          toast.error('Please add at least one question module.');
          return;
        }
      } else if (formData.questions.length === 0) {
        toast.error('Please add questions first.');
        return;
      }

      // Save proctoring config if interview exists
      if (interviewId) {
        try {
          setLoading(true);
          await axios.post(`/api/ai-interviews/${interviewId}/proctoring/config`, {
            browserActivityMonitoring: formData.proctoring.browserActivityMonitoring,
            responseDelayTracking: formData.proctoring.responseDelayTracking,
            speechPatternAnalysis: formData.proctoring.speechPatternAnalysis,
            transcriptAnalysis: formData.proctoring.transcriptAnalysis,
            multiSpeakerDetection: formData.proctoring.multiSpeakerDetection,
            voiceConsistencyCheck: formData.proctoring.voiceConsistencyCheck,
            randomInterruptQuestions: formData.proctoring.randomInterruptQuestions,
            // Video proctoring
            facePresenceDetection: formData.proctoring.facePresenceDetection,
            multipleFaceDetection: formData.proctoring.multipleFaceDetection,
            faceIdentityConsistency: formData.proctoring.faceIdentityConsistency,
            organisationId: jobPostingDetails?.organisationId || undefined,
            jobId: linkedJobPostingId || undefined,
          });
          toast.success('Proctoring settings saved successfully!');
        } catch (error) {
          console.error('Error saving proctoring config:', error);
          toast.error('Failed to save proctoring settings.');
          return;
        } finally {
          setLoading(false);
        }
      }

      setCurrentStep(3);
    } else if (targetStep === 4) {
      // Step 4: Invite - save reordered/edited questions before leaving Edit step
      if (advancedType === ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED) {
        if (rubricModules.length === 0) {
          toast.error('Please add at least one question module.');
          return;
        }
      } else if (formData.questions.length === 0) {
        toast.error('Please add questions first.');
        return;
      }
      if (interviewId) {
        try {
          setLoading(true);
          await updateInterviewWithQuestions();
        } catch (err) {
          console.error('Error saving questions:', err);
          return;
        } finally {
          setLoading(false);
        }
      }
      setCurrentStep(4);
    } else if (targetStep === 5) {
      // Step 5: Demo Preview - need questions
      if (advancedType === ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED) {
        if (rubricModules.length === 0) {
          toast.error('Please add at least one question module.');
          return;
        }
      } else if (formData.questions.length === 0) {
        toast.error('Please add questions first.');
        return;
      }
      setCurrentStep(5);
    } else {
      setCurrentStep(targetStep);
    }
  };

  const handleSaveToLibrary = useCallback(async () => {
    const isRubricBased = advancedType === ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED;
    
    if (!formData.title) {
      toast.error('Add a title before saving to library.');
      return;
    }
    if (!isRubricBased && (!Array.isArray(formData.questions) || formData.questions.length === 0)) {
      toast.error('Add at least one question before saving to library.');
      return;
    }
    if (isRubricBased && rubricModules.length === 0) {
      toast.error('Add at least one question module before saving to library.');
      return;
    }
    
    setSavingToLibrary(true);
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.context || '',
        role: getInterviewRole(),
        difficulty: 'Medium',
        estimatedTime: 15,
        category: formData.category || 'General',
        interviewId: interviewId || undefined,
        questions: isRubricBased ? [] : formData.questions,
        rubrics: isRubricBased ? rubricModules.map(m => ({
          name: m.name.trim(),
          questionsPerInterview: m.questionsPerInterview,
          questionBank: m.questionBank.map(({ _localId, ...rest }) => rest),
        })) : undefined,
        aiIntro: formData.aiIntro || '',
        aiOutro: formData.aiOutro || '',
        voiceId: formData.voiceId || undefined,
        config: {
          selectedTags: formData.selectedTags || [],
          context: formData.context || '',
          personality: formData.personality || '',
          maxQuestions: formData.maxQuestions,
          language: interviewLanguageCode,
          type: formData.type,
        },
        ...deriveAdvancedTypeForLanguage(advancedType, interviewLanguageCode),
        visibility: visibilityLevel,
        sharedWithDepartmentIds: visibilityLevel === 'shared' ? Array.from(selectedSharedDepartmentIds) : [],
      };
      await axios.post('/api/ai-interviews/save-to-library', payload);
      setShowSaveToLibraryConfirm(false);
      setHasSavedToLibrary(true);
      toast.success(
        'AI Interview saved to library. You can find it under Library → AI Interview Library.'
      );
      if (isLibraryCreateFlow && onClose) {
        onClose();
      }
    } catch (err) {
      logger.error('Save AI interview to library error:', err);
      toast.error(err.response?.data?.message || 'Failed to save AI Interview to library.');
    } finally {
      setSavingToLibrary(false);
    }
  }, [
    formData,
    getInterviewRole,
    interviewId,
    visibilityLevel,
    selectedSharedDepartmentIds,
    isLibraryCreateFlow,
    onClose,
    interviewLanguageCode,
    advancedType,
  ]);

  const {
    requestSave: requestLibraryEditSave,
    confirmModal: libraryEditImpactModal,
    reportSyncResult: reportLibrarySyncResult,
  } = useLibraryEditImpactConfirm(editLibraryInterviewId);

  const handleSaveLibraryEdit = useCallback(async () => {
    if (!editLibraryInterviewId) return;
    if (!formData.title || !Array.isArray(formData.questions) || formData.questions.length === 0) {
      toast.error('Add a title and at least one question before saving.');
      return;
    }
    setEditLibrarySaving(true);
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.context || '',
        role: getInterviewRole(),
        difficulty: 'Medium',
        estimatedTime: 15,
        category: formData.category || 'General',
        questions: formData.questions,
        aiIntro: formData.aiIntro || '',
        aiOutro: formData.aiOutro || '',
        voiceId: formData.voiceId || undefined,
        config: {
          selectedTags: formData.selectedTags || [],
          context: formData.context || '',
          personality: formData.personality || '',
          maxQuestions: formData.maxQuestions,
          language: interviewLanguageCode,
          type: formData.type,
        },
        ...deriveAdvancedTypeForLanguage(advancedType, interviewLanguageCode),
      };
      const result = await libraryApi.updateLibraryAIInterview(editLibraryInterviewId, payload);
      if (reportLibrarySyncResult(result)) {
        toast.success('AI Interview updated successfully.');
      }
      if (onClose) {
        onClose();
      }
    } catch (err) {
      logger.error('Update library AI interview error:', err);
      toast.error(err.response?.data?.message || 'Failed to update AI Interview.');
    } finally {
      setEditLibrarySaving(false);
    }
  }, [
    editLibraryInterviewId,
    formData,
    getInterviewRole,
    onClose,
    interviewLanguageCode,
    advancedType,
    reportLibrarySyncResult,
  ]);

  // Toggle tag selection
  const toggleTag = (tagId) => {
    setFormData((prev) => {
      const currentTags = prev.selectedTags;
      const isSelected = currentTags.includes(tagId);
      return {
        ...prev,
        selectedTags: isSelected ? currentTags.filter((t) => t !== tagId) : [...currentTags, tagId],
      };
    });
  };

  // Generate AI intro/outro based on settings
  // Note: {candidateName} will be replaced with the actual candidate's name during the interview
  const generateIntroOutro = useCallback(() => {
    const interviewTitle = formData.title || 'this interview';
    const personality = formData.personality;

    let introStyle = '';
    let outroStyle = '';

    switch (personality) {
      case 'friendly':
        introStyle = `Hi {candidateName}! Welcome to ${interviewTitle}. I'm your AI interviewer today, and I'm really excited to learn more about you. This is a conversational interview, so just relax and be yourself. Let me know when you are ready.`;
        outroStyle = `That was great, {candidateName}! Thank you so much for taking the time to chat with me today. We really appreciate your interest and will be in touch soon. Take care and have a wonderful day!`;
        break;
      case 'analytical':
        introStyle = `Welcome to ${interviewTitle}, {candidateName}. I am your AI interviewer. This interview will assess your qualifications through a series of structured questions. Please provide detailed, specific responses. Let me know when you are ready.`;
        outroStyle = `This concludes the interview, {candidateName}. Your responses have been recorded and will be evaluated. You will receive feedback within the specified timeframe. Thank you for your participation.`;
        break;
      case 'conversational':
        introStyle = `Hey {candidateName}! Welcome to ${interviewTitle}. I'm here to have a chat and get to know you better. Think of this as a friendly conversation rather than a formal interview. Let me know when you are ready.`;
        outroStyle = `Awesome, {candidateName}, that's a wrap! Thanks for sharing your experiences with me. It was great getting to know you. We'll follow up soon. Best of luck!`;
        break;
      default: // professional
        introStyle = `Welcome to ${interviewTitle}, {candidateName}. I am your AI interviewer. This interview is designed to evaluate your qualifications for this position. Please answer each question thoughtfully and thoroughly. Let me know when you are ready.`;
        outroStyle = `Thank you for completing this interview, {candidateName}. Your responses have been recorded and will be reviewed by our team. We appreciate your time and will be in contact regarding next steps.`;
    }

    setFormData((prev) => ({
      ...prev,
      aiIntro: prev.aiIntro || introStyle,
      aiOutro: prev.aiOutro || outroStyle,
    }));
  }, [formData.title, formData.personality]);

  // Email preview state
  const [emailPreview, setEmailPreview] = useState('');
  const emailPreviewEditedRef = useRef(false); // Track if user has manually edited

  // Generate keywords for a question based on its content and (for eligibility) the job description
  const generateKeywordsForQuestion = (question) => {
    const prompt = question.prompt?.toLowerCase() || '';
    const section = question.section || 'technical';

    // Default keywords based on section (used as a fallback)
    const sectionKeywords = {
      eligibility: ['location', 'availability', 'notice period', 'salary expectations'],
      technical: [
        'specific examples',
        'technical skills',
        'problem-solving',
        'implementation details',
      ],
      behavioral: [
        'teamwork',
        'leadership',
        'communication',
        'conflict resolution',
        'adaptability',
      ],
      cultural: ['values alignment', 'work ethic', 'collaboration', 'growth mindset'],
    };

    // Extract potential keywords from the question text itself
    const extractedKeywords = [];
    if (prompt.includes('experience')) extractedKeywords.push('experience');
    if (prompt.includes('notice')) extractedKeywords.push('notice period');
    if (prompt.includes('location')) extractedKeywords.push('location');
    if (prompt.includes('salary') || prompt.includes('ctc'))
      extractedKeywords.push('salary expectations');
    if (prompt.includes('relocate')) extractedKeywords.push('relocation');
    if (prompt.includes('shift')) extractedKeywords.push('shift timing');
    if (prompt.includes('challenge'))
      extractedKeywords.push('problem identification', 'solution approach', 'outcomes achieved');
    if (prompt.includes('team')) extractedKeywords.push('collaboration', 'team dynamics');
    if (prompt.includes('project'))
      extractedKeywords.push('technologies used', 'role played', 'results delivered');
    if (prompt.includes('manage') || prompt.includes('lead'))
      extractedKeywords.push('leadership style', 'delegation');
    if (prompt.includes('learn'))
      extractedKeywords.push('learning approach', 'continuous improvement');

    // For eligibility questions, prioritize structured data from the job posting
    const jdKeywords = [];
    if (section === 'eligibility' && jobPostingDetails) {
      const {
        location,
        locationType,
        experienceMin,
        experienceMax,
        experience,
        noticePeriod,
        salaryMin,
        salaryMax,
        salaryCurrency,
        skills,
      } = jobPostingDetails;

      if (location) {
        jdKeywords.push('location', location);
      }
      if (locationType) {
        jdKeywords.push(locationType === 'remote' ? 'remote' : locationType);
      }

      // Experience requirements
      if (typeof experienceMin === 'number' || typeof experienceMax === 'number') {
        jdKeywords.push('experience');
        if (typeof experienceMin === 'number') {
          jdKeywords.push(`${experienceMin}+ years experience`);
        }
        if (typeof experienceMax === 'number') {
          jdKeywords.push(`up to ${experienceMax} years experience`);
        }
      } else if (experience) {
        jdKeywords.push('experience', experience);
      }

      // Salary / CTC expectations
      if (typeof salaryMin === 'number' || typeof salaryMax === 'number') {
        jdKeywords.push('salary expectations');
        const currency = salaryCurrency || 'USD';
        if (typeof salaryMin === 'number' && typeof salaryMax === 'number') {
          jdKeywords.push(`salary ${currency} ${salaryMin} - ${salaryMax}`);
        } else if (typeof salaryMin === 'number') {
          jdKeywords.push(`minimum salary ${currency} ${salaryMin}`);
        } else if (typeof salaryMax === 'number') {
          jdKeywords.push(`maximum salary ${currency} ${salaryMax}`);
        }
      }

      if (noticePeriod) {
        jdKeywords.push('notice period', String(noticePeriod));
      }

      // Core skills as eligibility filters (must-have skills)
      if (Array.isArray(skills) && skills.length > 0) {
        jdKeywords.push('required skills');
        jdKeywords.push(...skills.slice(0, 10));
      }
    }

    // Combine JD-derived, question-derived, and section default keywords
    const combined = [
      ...jdKeywords,
      ...extractedKeywords,
      ...(sectionKeywords[section] || []),
    ];
    const keywords = [...new Set(combined)].filter(Boolean);
    return keywords.slice(0, 8);
  };

  const normalizeAIQuestion = (question, defaults = {}) => {
    if (!question) return null;
    const base = typeof question === 'string' ? { prompt: question } : { ...question };
    const prompt =
      typeof base.prompt === 'string' && base.prompt.trim().length
        ? base.prompt.trim()
        : typeof base.question === 'string'
          ? base.question.trim()
          : '';
    if (!prompt) return null;

    const allowedSections = [
      'intro',
      'eligibility',
      'technical',
      'behavioral',
      'cultural',
      'core',
      'wrap_up',
    ];
    const defaultSection = typeof defaults.section === 'string' ? defaults.section : 'core';
    const rawSection = typeof base.section === 'string' ? base.section : defaultSection;
    const section = allowedSections.includes(rawSection) ? rawSection : defaultSection;

    const defaultType = defaults.type === 'yes_no' ? 'yes_no' : 'short';
    const rawType = typeof base.type === 'string' ? base.type.toLowerCase() : '';
    const type = rawType === 'yes_no' ? 'yes_no' : rawType === 'short' ? 'short' : defaultType;

    let expectedAnswer = '';
    if (type === 'yes_no') {
      const raw = typeof base.expectedAnswer === 'string' ? base.expectedAnswer.toLowerCase() : '';
      expectedAnswer = raw.includes('no') ? 'No' : 'Yes';
    } else {
      expectedAnswer =
        typeof base.expectedAnswer === 'string'
          ? base.expectedAnswer.trim()
          : typeof defaults.expectedAnswer === 'string'
            ? defaults.expectedAnswer
            : '';
    }

    let expectedPoints = [];
    if (Array.isArray(base.expectedPoints) && base.expectedPoints.length > 0) {
      expectedPoints = base.expectedPoints
        .map((point) => {
          if (typeof point === 'string') return point.trim();
          if (point && typeof point.text === 'string') return point.text.trim();
          return '';
        })
        .filter(Boolean);
    }

    // Respect server-side validation metadata: if the server marked expected points as 'needs_review',
    // do NOT auto-derive expectedPoints client-side from expectedAnswer (this avoids showing generic fallbacks).
    const serverMeta = base.expectedPointsMeta || (defaults && defaults.expectedPointsMeta);
    const serverMarkedNeedsReview = serverMeta && serverMeta.validationStatus === 'needs_review';

    if (expectedPoints.length === 0 && type === 'short' && expectedAnswer && !serverMarkedNeedsReview) {
      expectedPoints = expectedAnswer
        .split(/\n|;|\.(?:\s|$)/)
        .map((point) => point.trim())
        .filter((point) => point.length >= 5)
        .slice(0, 5);
    }

    // Eligibility compensation: override expectedPoints with JD salary band when available.
    // This ensures the rubric for \"What is your expected compensation?\" is grounded
    // purely in the job's advertised range (e.g. \"750000 - 900000\").
    if (
      section === 'eligibility' &&
      type === 'short' &&
      jobPostingDetails &&
      (typeof jobPostingDetails.salaryMin === 'number' ||
        typeof jobPostingDetails.salaryMax === 'number')
    ) {
      const lowerPrompt = prompt.toLowerCase();
      const isCompQuestion =
        lowerPrompt.includes('expected compensation') ||
        lowerPrompt.includes('expected salary') ||
        lowerPrompt.includes('expected ctc') ||
        lowerPrompt.includes('compensation expectation');

      if (isCompQuestion) {
        const { salaryMin, salaryMax, salaryCurrency } = jobPostingDetails;
        let bandLabel = '';
        const currencyLabel = salaryCurrency ? ` ${salaryCurrency}` : '';

        if (typeof salaryMin === 'number' && typeof salaryMax === 'number') {
          bandLabel = `${salaryMin} - ${salaryMax}${currencyLabel}`;
        } else if (typeof salaryMin === 'number') {
          bandLabel = `${salaryMin}${currencyLabel}`;
        } else if (typeof salaryMax === 'number') {
          bandLabel = `${salaryMax}${currencyLabel}`;
        }

        if (bandLabel) {
          expectedPoints = [bandLabel];
        }
      }
    }

    // If server marked this question as needing review, preserve the question and return empty expectedPoints
    // instead of dropping it (so UI can show a "needs review" state).
    if (type === 'short' && expectedPoints.length === 0) {
      if (serverMarkedNeedsReview) {
        expectedPoints = [];
      } else {
        return null;
      }
    }

    const critical =
      type === 'yes_no'
        ? Boolean(typeof base.critical === 'boolean' ? base.critical : defaults.critical)
        : false;

    const allowFollowUps =
      section === 'eligibility'
        ? false
        : typeof base.allowFollowUps === 'boolean'
          ? base.allowFollowUps
          : typeof defaults.allowFollowUps === 'boolean'
            ? defaults.allowFollowUps
            : true;

    // Map section for keyword generation (core -> technical, wrap_up -> technical)
    const keywordSection = section === 'core' || section === 'wrap_up' ? 'technical' : section;

    // Auto-generate expected keywords if not provided
    const questionForKeywords = { prompt, section: keywordSection };
    const expectedKeywords =
      base.expectedKeywords &&
        Array.isArray(base.expectedKeywords) &&
        base.expectedKeywords.length > 0
        ? base.expectedKeywords
        : generateKeywordsForQuestion(questionForKeywords);

    return {
      prompt,
      type,
      expectedAnswer: type === 'yes_no' ? expectedAnswer || 'Yes' : expectedAnswer,
      expectedPoints: type === 'yes_no' ? [] : expectedPoints,
      critical,
      section,
      allowFollowUps,
      expectedKeywords,
    };
  };

  // Convert selected tags to role string for API compatibility
  function getInterviewRole() {
    const tags = formData.selectedTags;
    if (tags.length === 4) return 'holistic-assessment';
    if (tags.length === 1) {
      if (tags[0] === 'eligibility') return 'eligibility-screening';
      if (tags[0] === 'technical') return 'technical-competency';
      if (tags[0] === 'behavioral') return 'behavioral-assessment';
      if (tags[0] === 'cultural') return 'cultural-fit';
    }
    // Multiple tags but not all 4
    return tags.join('-');
  }

  // Get tag color classes
  const getTagColors = (tagId) => {
    const colors = {
      eligibility: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        border: 'border-yellow-300',
        activeBg: 'bg-yellow-500',
      },
      technical: {
        bg: 'bg-blue-100',
        text: 'text-blue-700',
        border: 'border-blue-300',
        activeBg: 'bg-blue-500',
      },
      behavioral: {
        bg: 'bg-green-100',
        text: 'text-green-700',
        border: 'border-green-300',
        activeBg: 'bg-green-500',
      },
      cultural: {
        bg: 'bg-purple-100',
        text: 'text-purple-700',
        border: 'border-purple-300',
        activeBg: 'bg-purple-500',
      },
    };
    return colors[tagId] || colors.technical;
  };

  const animateQuestions = async (questions) => {
    setIsGenerating(true);
    setGeneratingQuestions(questions);
    setIsGenerating(false);
    setFormData((prev) => ({ ...prev, questions }));
  };

  const accents = [
    { value: 'professional', label: 'Professional' },
    { value: 'friendly', label: 'Friendly' },
    { value: 'analytical', label: 'Analytical' },
    { value: 'conversational', label: 'Conversational' },
  ];

  const fetchJobPostingDetails = useCallback(async () => {
    try {
      const response = await axios.get('/api/job-posts/jobs/' + linkedJobPostingId);
      setJobPostingDetails(response.data.job);
    } catch (error) {
      console.error('Error fetching job posting details:', error);
    }
  }, [linkedJobPostingId]);

  // ---- JD upload helpers (library AI flow) ----
  const MAX_JD_FILE_SIZE_MB = 10;
  const MAX_JD_FILE_SIZE_BYTES = MAX_JD_FILE_SIZE_MB * 1024 * 1024;
  const MAX_QUESTION_FILE_SIZE_MB = 10;
  const MAX_QUESTION_FILE_SIZE_BYTES = MAX_QUESTION_FILE_SIZE_MB * 1024 * 1024;

  const validateJdFile = (file) => {
    const lowerName = (file.name || '').toLowerCase();
    const isPdf = lowerName.endsWith('.pdf');
    const isDocx = lowerName.endsWith('.docx');
    const isDoc = lowerName.endsWith('.doc');
    if (!isPdf && !isDocx && !isDoc) {
      toast.error('Please upload a PDF, DOC, or DOCX file');
      return false;
    }
    if (file.size > MAX_JD_FILE_SIZE_BYTES) {
      toast.error(`File size must be under ${MAX_JD_FILE_SIZE_MB}MB`);
      return false;
    }
    return true;
  };

  const processJdFile = async (file) => {
    try {
      setJdProcessing(true);
      const formDataUpload = new FormData();
      formDataUpload.append('jdFile', file);

      const response = await axios.post('/api/job-posts/generate-from-jd', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success && response.data.jobPosting) {
        const generated = response.data.jobPosting;
        const normalizedJobData = {
          title: generated.title || '',
          description: generated.description || '',
          keyResponsibilities: generated.keyResponsibilities || '',
          experience: generated.experience || '',
          skills: Array.isArray(generated.skills) ? generated.skills : [],
          keyAttributes: generated.keyAttributes || '',
          location: generated.location || '',
          candidateQualification: generated.candidateQualification || '',
          noticePeriod: generated.noticePeriod || '',
          experienceMin: generated.experienceMin ?? '',
          experienceMax: generated.experienceMax ?? '',
        };
        setJdExtractedJobData(normalizedJobData);
        setJdExtractedSummary({
          title: normalizedJobData.title,
          location: normalizedJobData.location,
          experience: normalizedJobData.experience,
        });

        // Auto-fill only title; keep Additional Context user-authored
        setFormData((prev) => ({
          ...prev,
          title: prev.title || normalizedJobData.title || prev.title,
        }));

        toast.success('JD processed successfully! Job details extracted.');
      } else {
        toast.error(response.data.message || 'Failed to process JD file');
        setJdFile(null);
        setJdExtractedJobData(null);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error processing JD file for AI Interview:', error);
      toast.error('Failed to process JD file. Please try again.');
      setJdFile(null);
      setJdExtractedJobData(null);
    } finally {
      setJdProcessing(false);
    }
  };

  const handleJdFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      event.target.value = '';
      return;
    }
    if (!validateJdFile(file)) {
      event.target.value = '';
      return;
    }
    setJdFile(file);
    event.target.value = '';
    processJdFile(file);
  };

  const handleJdFileDrop = (e) => {
    e.preventDefault();
    setIsDraggingJd(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!validateJdFile(file)) return;
    setJdFile(file);
    if (jdFileInputRef.current) jdFileInputRef.current.value = '';
    processJdFile(file);
  };

  const handleJdDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingJd(true);
  };

  const handleJdDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingJd(false);
  };

  const handleJdRemoveFile = () => {
    setJdFile(null);
    setJdExtractedSummary(null);
    setJdExtractedJobData(null);
    if (jdFileInputRef.current) jdFileInputRef.current.value = '';
  };

  const validateQuestionFile = (file) => {
    const lowerName = (file.name || '').toLowerCase();
    const isPdf = lowerName.endsWith('.pdf');
    const isDocx = lowerName.endsWith('.docx');
    const isDoc = lowerName.endsWith('.doc');
    const isTxt = lowerName.endsWith('.txt');
    const isCsv = lowerName.endsWith('.csv');
    if (!isPdf && !isDocx && !isDoc && !isTxt && !isCsv) {
      toast.error('Please upload a PDF, DOC, DOCX, TXT, or CSV file');
      return false;
    }
    if (file.size > MAX_QUESTION_FILE_SIZE_BYTES) {
      toast.error(`Question file size must be under ${MAX_QUESTION_FILE_SIZE_MB}MB`);
      return false;
    }
    return true;
  };

  const handleQuestionFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (!validateQuestionFile(file)) return;
    setQuestionFile(file);
    toast.success('Question file added. Click Create questions to process it.');
  };

  const handleQuestionFileDrop = (e) => {
    e.preventDefault();
    setIsDraggingQuestionFile(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!validateQuestionFile(file)) return;
    setQuestionFile(file);
    if (questionFileInputRef.current) questionFileInputRef.current.value = '';
    toast.success('Question file added. Click Create questions to process it.');
  };

  const handleQuestionFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingQuestionFile(true);
  };

  const handleQuestionFileDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingQuestionFile(false);
  };

  const handleQuestionFileRemove = () => {
    setQuestionFile(null);
    if (questionFileInputRef.current) questionFileInputRef.current.value = '';
  };

  // Fetch job posting details when component mounts
  useEffect(() => {
    if (linkedJobPostingId) {
      fetchJobPostingDetails();
    }
  }, [linkedJobPostingId, fetchJobPostingDetails]);

  // Load library AI interview template when editLibraryInterviewId is set
  useEffect(() => {
    if (editLibraryInterviewId) {
      setEditLibraryLoading(true);
      libraryApi
        .getAIInterviewById(editLibraryInterviewId)
        .then((template) => {
          // Normalize legacy values: stored language may be 'en' | 'hi' | 'hi-en' |
          // 'kn' | 'ta' | 'bn' | 'te' | 'mr' | 'gu' | 'ml' | 'pa' | 'tl' | 'fr' | 'english' | 'hindi'.
          // Keep in sync with LANGUAGE_OPTIONS / JobScreeningsPage validLangCodes.
          const tplLanguage = template.config?.language;
          const validLangCodes = ['en', 'hi', 'hi-en', 'en-hi', 'kn', 'ta', 'bn', 'te', 'mr', 'gu', 'ml', 'pa', 'tl', 'fr'];
          let normalizedLangCode =
            tplLanguage === 'hi' || tplLanguage === 'hindi'
              ? 'hi'
              : tplLanguage === 'hi-en'
                ? 'hi-en'
                : validLangCodes.includes(tplLanguage)
                  ? tplLanguage
                  : 'en';
          if (template.advancedType === ADVANCED_INTERVIEW_TYPES.MULTI_LANGUAGE && normalizedLangCode === 'en') {
            // Legacy Multi-Language templates that pre-date Hinglish default to Hindi.
            normalizedLangCode = 'hi';
          }
          setFormData((prev) => ({
            ...prev,
            title: template.title || '',
            category: template.category || 'General',
            selectedTags: template.config?.selectedTags || [],
            context: template.description || template.config?.context || '',
            questions: template.questions || [],
            type: template.config?.type || 'voice',
            // Keep legacy `language` aligned with the canonical code so the two
            // fields cannot drift on subsequent saves.
            language: normalizedLangCode,
            personality: template.config?.personality || 'professional',
            maxQuestions: template.config?.maxQuestions || 6,
            aiIntro: template.aiIntro || '',
            aiOutro: template.aiOutro || '',
            // Restore saved voice; fall back to language-appropriate default
            voiceId: currentVoiceId(template.voiceId) || prev.voiceId,
          }));
          if (template.advancedType) {
            setAdvancedType(template.advancedType);
          }
          if (template.rubrics && template.rubrics.length > 0) {
            setRubricModules(
              template.rubrics.map((r) => ({
                _localId: Math.random().toString(36).substring(2, 11),
                name: r.name || '',
                questionsPerInterview: r.questionsPerInterview ?? 1,
                questionBank: (r.questionBank || []).map((q) => ({
                  ...q,
                  _localId: Math.random().toString(36).substring(2, 11),
                })),
              }))
            );
          }
          setInterviewLanguageCode(normalizedLangCode);
        })
        .catch((err) => {
          logger.error('Error loading library AI interview:', err);
          toast.error('Failed to load AI Interview template.');
          if (onClose) onClose();
        })
        .finally(() => {
          setEditLibraryLoading(false);
        });
    }
  }, [editLibraryInterviewId, onClose]);

  // In screening modal flow, keep interview title empty so user must enter it.

  // When standalone and job posting loads, use job title as interview title if empty
  useEffect(() => {
    if (!screeningName?.trim() && jobPostingDetails?.title) {
      setFormData((prev) =>
        prev.title ? prev : { ...prev, title: `${jobPostingDetails.title} AI Interview` }
      );
    }
  }, [screeningName, jobPostingDetails?.title]);

  // Generate email preview based on form data
  const generateEmailPreview = useCallback(() => {
    const interviewTitle = formData.title || '[Interview Title]';
    const candidateName = '[Candidate Name]';
    const inviteLink = '[Interview Link]';
    const companyName = jobPostingDetails?.company || '[Company Name]';
    const jobTitle = jobPostingDetails?.title || '[Position]';
    const categoryLabels = formData.selectedTags
      .map((t) => {
        const tag = questionTags.find((qt) => qt.id === t);
        return tag ? tag.label : t;
      })
      .join(', ');

    const expiryText =
      formData.inviteExpiry === '1-week'
        ? '7 days'
        : formData.inviteExpiry === '2-weeks'
          ? '14 days'
          : formData.inviteExpiry === '1-month'
            ? '30 days'
            : formData.inviteExpiry === '2-months'
              ? '60 days'
              : '90 days';

    const preview = `Subject: Interview Invitation - ${jobTitle} at ${companyName}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🎯 ${companyName} - Interview Invitation
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Hi ${candidateName},

Thank you for your interest in the ${jobTitle} position at ${companyName}. We're excited to move forward with your application!

📋 INTERVIEW DETAILS
────────────────────
Interview: ${interviewTitle}
Position: ${jobTitle}
Company: ${companyName}
Format: AI-Powered Interview (${formData.type === 'voice' ? 'Voice' : 'Video'})
${categoryLabels ? `Focus Areas: ${categoryLabels}` : ''}
Estimated Time: 15-20 minutes
Valid Until: ${expiryText} from now

🚀 START YOUR INTERVIEW
────────────────────
Click the link below to begin:
${inviteLink}

💡 TIPS FOR SUCCESS
────────────────────
• Find a quiet space with stable internet
• Use headphones for better audio quality
• Speak clearly and take your time
• Be yourself - we want to know the real you!

📞 NEED HELP?
────────────────────
If you have any questions or technical issues, please reply to this email.

Best regards,
The ${companyName} Hiring Team

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Powered by TalentCrux AI Interview Platform
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`;

    return preview;
  }, [
    formData.title,
    formData.selectedTags,
    formData.type,
    formData.inviteExpiry,
    questionTags,
    jobPostingDetails,
  ]);

  // Initialize email preview on mount
  useEffect(() => {
    const initialPreview = generateEmailPreview();
    setEmailPreview(initialPreview);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update email preview when key form fields change (only if user hasn't edited)
  useEffect(() => {
    // Only auto-update if user hasn't manually edited
    if (!emailPreviewEditedRef.current) {
      setEmailPreview(generateEmailPreview());
    }
  }, [
    formData.title,
    formData.selectedTags,
    formData.type,
    formData.inviteExpiry,
    jobPostingDetails,
    generateEmailPreview,
  ]);

  // Generate intro/outro when personality or title changes
  useEffect(() => {
    if (formData.title && formData.personality) {
      generateIntroOutro();
    }
  }, [formData.title, formData.personality, generateIntroOutro]);

  const hasAiGenerationSource = Boolean(
    linkedJobPostingId || jdExtractedJobData || jdExtractedSummary
  );
  const contextValue = (formData.context || '').trim();
  const isContextRequired = !(creationMode === CREATION_MODE.MANUAL && isLibraryCreateFlow) && advancedType !== ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED;
  const shouldShowJdModalCta = creationMode === CREATION_MODE.AI && !linkedJobPostingId;

  const handleGetAIRecommendations = async () => {
    // Library create flow (no job posting): allow generation from title + context + categories
    if (!linkedJobPostingId && !isLibraryCreateFlow) {
      toast.error('No job posting linked to this AI interview');
      return;
    }

    const isManualFilePath = isLibraryCreateFlow && creationMode === CREATION_MODE.MANUAL;
    if (!isManualFilePath && formData.selectedTags.length === 0) {
      toast.error('Please select at least one question category');
      return;
    }
    if (isContextRequired && !contextValue) {
      toast.error('Please add Context to continue.');
      return;
    }
    if (isManualFilePath && !questionFile) {
      toast.error('Upload a Question Bank file.');
      return;
    }

    try {
      setLoadingRecommendations(true);
      setIsGenerating(true);
      setPendingQuestionLocalId(null);
      setExpandedQuestionIndex(null);
      pendingQuestionLockRef.current = false;
      setGeneratingQuestions([]);

      const effectiveTitle =
        formData.title ||
        (jobPostingDetails?.title ? `${jobPostingDetails.title} AI Interview` : 'AI Interview');
      const maxQuestionsForGeneration = isManualFilePath
        ? MAX_AI_INTERVIEW_GENERATED_QUESTIONS
        : Math.min(
            MAX_AI_INTERVIEW_GENERATED_QUESTIONS,
            Math.max(1, Number(formData.maxQuestions) || 6)
          );
      const constraints = {
        title: effectiveTitle,
        role: getInterviewRole(), // Use tag-based role
        selectedTags: formData.selectedTags, // Send selected tags
        context: formData.context || undefined,
        personality: formData.personality || undefined,
        eligibility: formData.selectedTags.includes('eligibility')
          ? formData.eligibility
          : undefined,
        maxQuestions: maxQuestionsForGeneration,
      };
      if (formData.duration != null && formData.duration !== '') {
        constraints.duration = Number(formData.duration) || undefined;
      }
      let response;
      const shouldUseQuestionFilePath =
        isLibraryCreateFlow && creationMode === CREATION_MODE.MANUAL && questionFile;
      if (shouldUseQuestionFilePath) {
        const formPayload = new FormData();
        formPayload.append('questionsFile', questionFile);
        formPayload.append('constraints', JSON.stringify(constraints));
        if (linkedJobPostingId) {
          formPayload.append('jobPostingId', linkedJobPostingId);
        } else if (jdExtractedJobData) {
          formPayload.append('jobPostingData', JSON.stringify(jdExtractedJobData));
        }
        response = await axios.post(
          '/api/ai-interviews/recommendations-from-question-file',
          formPayload,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
      } else {
        let requestData;
        if (linkedJobPostingId) {
          requestData = { jobPostingId: linkedJobPostingId, constraints };
        } else if (jdExtractedJobData) {
          requestData = { jobPostingData: jdExtractedJobData, constraints };
        } else {
          requestData = { constraints };
        }
        response = await axios.post('/api/ai-interviews/recommendations', requestData);
      }

      logger.log('🔍 API Response:', {
        success: response.data.success,
        hasRecommendations: !!response.data.recommendations,
        recommendationsKeys: response.data.recommendations
          ? Object.keys(response.data.recommendations)
          : [],
      });

      if (response.data.success) {
        setAiRecommendations(response.data.recommendations);

        // Extract and animate questions from ALL sections
        const recommendations = response.data.recommendations;
        const sections = recommendations.sections || {};

        logger.log('📦 Raw sections data:', sections);
        logger.log('📦 Recommendations structure:', {
          hasSections: !!recommendations.sections,
          hasQuestions: !!recommendations.questions,
          sectionsKeys: Object.keys(sections),
          questionsLength: Array.isArray(recommendations.questions)
            ? recommendations.questions.length
            : 'not an array',
        });

        // Extract questions from all possible sections (eligibility, technical, behavioral, cultural, core)
        const eligibility = (sections.eligibility || []).map((q) => ({
          ...q,
          section: q.section || 'eligibility',
        }));
        const technical = (sections.technical || []).map((q) => ({
          ...q,
          section: q.section || 'technical',
        }));
        const behavioral = (sections.behavioral || []).map((q) => ({
          ...q,
          section: q.section || 'behavioral',
        }));
        const cultural = (sections.cultural || []).map((q) => ({
          ...q,
          section: q.section || 'cultural',
        }));
        const core = (sections.core || []).map((q) => ({ ...q, section: q.section || 'core' }));

        logger.log('📦 Extracted sections:', {
          eligibility: eligibility.length,
          technical: technical.length,
          behavioral: behavioral.length,
          cultural: cultural.length,
          core: core.length,
        });

        // Combine all questions, maintaining order: eligibility first, then by category, then core
        const allQuestions = [...eligibility, ...technical, ...behavioral, ...cultural, ...core];

        // Also check if questions are in a combined array (backup/alternative structure)
        const combinedQuestions = (recommendations.questions || []).map((q) => {
          // Ensure questions from combined array have section field if missing
          if (!q.section && q.prompt) {
            return q; // Will be inferred later
          }
          return q;
        });

        // Get selected tags for section inference if needed
        const selectedTags = formData.selectedTags || [];

        // Use combined questions if sections are empty, otherwise use sections
        const questionsToUse = allQuestions.length > 0 ? allQuestions : combinedQuestions;

        logger.log('📊 Questions to use:', {
          fromSections: allQuestions.length,
          fromCombined: combinedQuestions.length,
          total: questionsToUse.length,
          sectionsBreakdown: {
            technical: allQuestions.filter((q) => q.section === 'technical').length,
            behavioral: allQuestions.filter((q) => q.section === 'behavioral').length,
            cultural: allQuestions.filter((q) => q.section === 'cultural').length,
            eligibility: allQuestions.filter((q) => q.section === 'eligibility').length,
          },
        });

        if (questionsToUse.length > 0) {
          // Animate questions appearing one by one
          // Preserve the section from the question itself if it exists
          const normalizedQuestions = questionsToUse
            .map((q) => {
              // Determine section - use question's section if available, otherwise infer from content
              let section = q.section;
              if (!section) {
                // Infer from question content using keyword matching
                const promptLower = (q.prompt || '').toLowerCase();

                // Check eligibility indicators (location/comp/notice/work auth/education)
                if (
                  selectedTags.includes('eligibility') &&
                  (promptLower.includes('salary') ||
                    promptLower.includes('compensation') ||
                    promptLower.includes('ctc') ||
                    promptLower.includes('notice period') ||
                    promptLower.includes('availability') ||
                    promptLower.includes('relocate') ||
                    promptLower.includes('currently reside') ||
                    promptLower.includes('currently live') ||
                    promptLower.includes('where do you live') ||
                    promptLower.includes('where are you located') ||
                    promptLower.includes('where are you based') ||
                    promptLower.includes('based in') ||
                    promptLower.includes('degree') ||
                    promptLower.includes('qualification') ||
                    promptLower.includes('bachelor') ||
                    promptLower.includes('masters') ||
                    promptLower.includes('mca') ||
                    promptLower.includes('btech') ||
                    promptLower.includes('b.tech') ||
                    promptLower.includes('b.e') ||
                    promptLower.includes('years of experience') ||
                    promptLower.includes('how many years') ||
                    (promptLower.includes('years') && promptLower.includes('experience')))
                ) {
                  section = 'eligibility';
                }
                // Check technical indicators
                else if (
                  selectedTags.includes('technical') &&
                  (promptLower.includes('technical') ||
                    promptLower.includes('code') ||
                    promptLower.includes('programming') ||
                    promptLower.includes('system design') ||
                    promptLower.includes('algorithm') ||
                    promptLower.includes('technology') ||
                    promptLower.includes('debug') ||
                    (promptLower.includes('problem-solving') &&
                      !promptLower.includes('tell me about a time')))
                ) {
                  section = 'technical';
                }
                // Check behavioral indicators
                else if (
                  selectedTags.includes('behavioral') &&
                  (promptLower.includes('tell me about a time') ||
                    promptLower.includes('example') ||
                    promptLower.includes('situation') ||
                    promptLower.includes('conflict') ||
                    promptLower.includes('teamwork') ||
                    promptLower.includes('handled') ||
                    (promptLower.includes('challenge') && !promptLower.includes('technical')))
                ) {
                  section = 'behavioral';
                }
                // Check cultural indicators
                else if (
                  selectedTags.includes('cultural') &&
                  (promptLower.includes('work style') ||
                    promptLower.includes('culture') ||
                    promptLower.includes('values') ||
                    promptLower.includes('prefer') ||
                    promptLower.includes('ideal') ||
                    promptLower.includes('collaboration') ||
                    promptLower.includes('work-life balance'))
                ) {
                  section = 'cultural';
                }
                // Default fallback
                else {
                  section = 'core';
                }
              }

              // Determine type
              // Default eligibility questions to detailed answers, but allow the model to override via q.type
              const type = q.type || (section === 'eligibility' ? 'short' : 'short');

              return normalizeAIQuestion(q, {
                section: section,
                type: type,
              });
            })
            .filter(Boolean);

          logger.log(`✅ Extracted ${normalizedQuestions.length} questions:`, {
            technical: normalizedQuestions.filter((q) => q.section === 'technical').length,
            behavioral: normalizedQuestions.filter((q) => q.section === 'behavioral').length,
            cultural: normalizedQuestions.filter((q) => q.section === 'cultural').length,
            eligibility: normalizedQuestions.filter((q) => q.section === 'eligibility').length,
            core: normalizedQuestions.filter((q) => q.section === 'core').length,
          });

          await animateQuestions(normalizedQuestions);
          toast.success(`Successfully generated ${normalizedQuestions.length} questions!`);

          // Update interview with the generated questions
          await updateInterviewWithQuestions();
        } else {
          console.error('❌ No questions found in recommendations:', {
            sections: Object.keys(sections),
            sectionsContent: sections,
            combinedQuestions: combinedQuestions.length,
            fullRecommendations: recommendations,
          });
          toast.error(
            'No questions were generated. Please check the browser console for details and try again.'
          );
        }
      } else {
        console.error('❌ API returned success=false:', response.data);
        toast.error('Failed to generate questions. Please try again.');
      }
    } catch (error) {
      console.error('Error getting AI recommendations:', error);

      const errorResponse = error && error.response ? error.response : null;
      const errorStatus = errorResponse && errorResponse.status ? errorResponse.status : null;
      if (errorStatus === 429) {
        toast.error('AI service is temporarily unavailable. Please try again in a few minutes.');
      } else if (errorStatus === 503) {
        toast.error('AI service is not configured. Please contact support.');
      } else {
        toast.error('Failed to generate questions. Please try again.');
      }
    } finally {
      setLoadingRecommendations(false);
      setIsGenerating(false);
    }
  };

  const handleQuestionDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination || destination.index === source.index) {
      return;
    }

    setFormData((prev) => {
      const updatedQuestions = Array.from(prev.questions || []);
      const [moved] = updatedQuestions.splice(source.index, 1);
      updatedQuestions.splice(destination.index, 0, moved);
      return {
        ...prev,
        questions: updatedQuestions,
      };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const createEmptyQuestion = (overrides = {}) => {
    // localId is used only on the client to track the placeholder question that
    // must be saved before the user can add another question.
    const localId =
      overrides.localId ||
      (typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `q_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);

    const raw =
      typeof overrides.section === 'string' ? overrides.section.trim() : '';
    const section = QUESTION_CATEGORY_SECTIONS.includes(raw) ? raw : 'technical';

    return {
      localId,
      prompt: '',
      type: 'short',
      expectedAnswer: '',
      expectedPoints: [],
      critical: false,
      ...overrides,
      section,
      allowFollowUps: section === 'eligibility' ? false : true,
    };
  };

  const updateQuestion = (index, changes) => {
    setFormData((prev) => {
      const questions = [...prev.questions];
      const existing = questions[index] || createEmptyQuestion();
      let next = { ...existing, ...changes };

      if (changes && typeof changes.section === 'string') {
        const s = QUESTION_CATEGORY_SECTIONS.includes(changes.section)
          ? changes.section
          : 'technical';
        next.section = s;
        next.allowFollowUps = s === 'eligibility' ? false : true;
      }

      // Normalize question fields when type changes
      if (changes && typeof changes.type === 'string' && changes.type !== existing.type) {
        const nextType = changes.type === 'yes_no' ? 'yes_no' : 'short';
        if (nextType === 'yes_no') {
          next = {
            ...next,
            type: 'yes_no',
            expectedPoints: [], // yes/no uses expectedAnswer only
            expectedAnswer:
              typeof existing.expectedAnswer === 'string' && existing.expectedAnswer.trim()
                ? existing.expectedAnswer.trim().toLowerCase().startsWith('n')
                  ? 'No'
                  : 'Yes'
                : 'Yes',
            critical: false, // user must explicitly mark critical
          };
        } else {
          next = {
            ...next,
            type: 'short',
            critical: false, // critical applies to yes/no only
            expectedAnswer: typeof existing.expectedAnswer === 'string' ? existing.expectedAnswer : '',
            expectedPoints: Array.isArray(existing.expectedPoints) ? existing.expectedPoints : [],
          };
        }
      }

      // Enforce critical only for yes/no
      if (next.type !== 'yes_no' && next.critical) {
        next.critical = false;
      }

      questions[index] = next;
      return {
        ...prev,
        questions,
      };
    });
  };

  const handleAutoGenerateExpected = async (index) => {
    const question = formData.questions[index];
    if (!question || !question.prompt?.trim()) {
      toast.error('Please enter the question text first');
      return;
    }

    // If existing answer data is present, surface a styled confirm modal
    // instead of the default browser confirm() dialog.
    let hasExisting = false;
    if (question.type === 'yes_no') {
      hasExisting = !!question.expectedAnswer;
    } else {
      hasExisting = Array.isArray(question.expectedPoints) && question.expectedPoints.length > 0 && question.expectedPoints.some(p => p?.trim());
    }
    if (hasExisting) {
      setOverwriteExpectedConfirm({ open: true, index });
      return;
    }

    await runAutoGenerateExpected(index);
  };

  const runAutoGenerateExpected = async (index) => {
    const question = formData.questions[index];
    if (!question || !question.prompt?.trim()) {
      return;
    }

    setAutoGeneratingIndices(prev => new Set([...prev, index]));
    try {
      const { data } = await axios.post('/api/ai-interviews/generate-expected', {
        question: {
          prompt: question.prompt,
          type: question.type,
          section: question.section,
        },
        jobPostingId: linkedJobPostingId || formData.jobPostingId,
      });

      console.log('[AutoExpected] Response:', JSON.stringify(data, null, 2));

      if (!data.success) {
        toast.error(data.message || 'AI could not generate an answer for this question');
        return;
      }

      if (data.success && data.result) {
        let filled = false;
        if (question.type === 'yes_no') {
          const answer = data.result.expectedAnswer;
          if (answer && typeof answer === 'string' && answer.trim()) {
            updateQuestion(index, { expectedAnswer: answer.trim() });
            filled = true;
          }
        } else {
          const points = Array.isArray(data.result.expectedPoints)
            ? data.result.expectedPoints.filter(p => typeof p === 'string' && p.trim())
            : [];
          if (points.length > 0) {
            updateQuestion(index, { expectedPoints: points });
            filled = true;
          }
        }
        if (filled) {
          toast.success('Expected answer generated');
        } else {
          toast.error('AI could not generate an answer. Check server logs for details.');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate expected answer');
      console.error('Auto generate expected error:', error);
    } finally {
      setAutoGeneratingIndices(prev => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    }
  };

  const handleAddQuestion = () => {
    if (pendingQuestionLockRef.current || pendingQuestionLocalId) {
      toast.error('Please save your newly created question before adding another.');
      return;
    }

    pendingQuestionLockRef.current = true;

    setFormData((prev) => {
      const prevQuestions = Array.isArray(prev.questions) ? prev.questions : [];

      if (prevQuestions.length >= MAX_INTERVIEW_QUESTIONS_TOTAL) {
        toast.error(
          `Maximum ${MAX_INTERVIEW_QUESTIONS_TOTAL} questions allowed. Remove a question before adding another.`
        );
        pendingQuestionLockRef.current = false;
        return prev;
      }

      const newQuestion = createEmptyQuestion({
        section: defaultSectionFromInterviewTags(prev.selectedTags),
      });
      const nextQuestions = [...prevQuestions, newQuestion];
      const newIndex = nextQuestions.length - 1;

      // Open only the newly created question.
      setExpandedQuestionIndex(newIndex);
      setPendingQuestionLocalId(newQuestion.localId);

      return {
        ...prev,
        questions: nextQuestions,
      };
    });
  };

  const handleRemoveQuestion = (index) => {
    const removingQuestion = formData?.questions?.[index];
    const isRemovingPending = Boolean(
      pendingQuestionLocalId && removingQuestion?.localId === pendingQuestionLocalId
    );

    if (isRemovingPending) {
      setPendingQuestionLocalId(null);
      pendingQuestionLockRef.current = false;
      setExpandedQuestionIndex(null);
    } else {
      // Keep the currently expanded accordion stable when removing earlier items.
      setExpandedQuestionIndex((prevExpanded) => {
        if (prevExpanded === null) return prevExpanded;
        if (prevExpanded === index) return null;
        if (prevExpanded > index) return prevExpanded - 1;
        return prevExpanded;
      });
    }

    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };

  // Create interview function that can be called during step navigation
  const handleCreateInterviewForStep = async () => {
    if (!interviewId) {
      // Create a minimal interview with basic questions to get an ID
      const effectiveTitle =
        formData.title ||
        (jobPostingDetails?.title ? `${jobPostingDetails.title} AI Interview` : 'AI Interview');

      const payload = {
        title: effectiveTitle,
        role: getInterviewRole(),
        context: formData.context,
        questions: advancedType === ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED ? [] : (formData.questions && formData.questions.length > 0
          ? formData.questions
          : [{
            prompt: 'Sample question - will be replaced with generated questions',
            type: 'short',
            expectedAnswer: 'Sample answer',
            expectedPoints: ['Sample point'],
            critical: false,
            section: 'technical',
            allowFollowUps: true,
          }]), // Add dummy question if no questions exist yet
        rubrics: advancedType === ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED ? rubricModules.map(m => ({
          name: m.name.trim(),
          questionsPerInterview: m.questionsPerInterview,
          questionBank: m.questionBank.map(({ _localId, ...rest }) => rest),
        })) : undefined,
        type: formData.type,
        voiceId: formData.voiceId,
        jobPostingId: formData.jobPostingId,
        deliveryMode: formData.deliveryMode,
        inviteExpiry: formData.inviteExpiry,
        selectedTags: formData.selectedTags,
        eligibility: formData.selectedTags.includes('eligibility')
          ? formData.eligibility
          : undefined,
        aiIntro: formData.aiIntro,
        aiOutro: formData.aiOutro,
        config: {
          personality: formData.personality,
          language: interviewLanguageCode,
        },
        ...deriveAdvancedTypeForLanguage(advancedType, interviewLanguageCode),
      };

      const response = await axios.post('/api/ai-interviews/create', payload);
      const responseInfo = response && response.data ? response.data : {};
      const responseInterview = responseInfo && responseInfo.interview ? responseInfo.interview : null;
      const responseDirectId = responseInfo && responseInfo._id ? responseInfo._id : null;
      const responseInterviewId = responseInterview && responseInterview._id ? responseInterview._id : null;
      const createdInterviewId = responseInterviewId || responseDirectId || '';

      setInterviewId(createdInterviewId);
      return createdInterviewId;
    }
    return interviewId;
  };

  // Update interview with questions after they are generated
  const updateInterviewWithQuestions = async () => {
    const isRubricBased = advancedType === ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED;
    if (!interviewId || (!isRubricBased && (!formData.questions || formData.questions.length === 0))) {
      return;
    }

    try {
      const structuredQuestions = formData.questions.map((question) => {
        const prompt = (question.prompt || '').trim();
        const type = question.type === 'yes_no' ? 'yes_no' : 'short';
        const expectedPoints = Array.isArray(question.expectedPoints)
          ? question.expectedPoints.map((point) => point.trim()).filter(Boolean)
          : [];
        const expectedAnswer =
          type === 'yes_no'
            ? (question.expectedAnswer || 'Yes').toLowerCase().startsWith('n')
              ? 'No'
              : 'Yes'
            : (question.expectedAnswer || '').trim();
        const critical = type === 'yes_no' ? Boolean(question.critical) : false;
        const section = typeof question.section === 'string' ? question.section : 'core';
        const allowFollowUps =
          section === 'eligibility' ? false : question.allowFollowUps !== false;
        return {
          prompt,
          type,
          expectedAnswer,
          expectedPoints,
          critical,
          section,
          allowFollowUps,
        };
      });

      const payload = {
        title: formData.title || (jobPostingDetails?.title ? `${jobPostingDetails.title} AI Interview` : 'AI Interview'),
        role: getInterviewRole(),
        context: formData.context,
        questions: isRubricBased ? [] : structuredQuestions,
        rubrics: isRubricBased ? rubricModules.map(m => ({
          name: m.name.trim(),
          questionsPerInterview: m.questionsPerInterview,
          questionBank: m.questionBank.map(({ _localId, ...rest }) => rest),
        })) : undefined,
        type: formData.type,
        voiceId: formData.voiceId,
        jobPostingId: formData.jobPostingId,
        deliveryMode: formData.deliveryMode,
        inviteExpiry: formData.inviteExpiry,
        selectedTags: formData.selectedTags,
        eligibility: formData.selectedTags.includes('eligibility')
          ? formData.eligibility
          : undefined,
        aiIntro: formData.aiIntro,
        aiOutro: formData.aiOutro,
        config: {
          personality: formData.personality,
          language: interviewLanguageCode,
        },
        ...deriveAdvancedTypeForLanguage(advancedType, interviewLanguageCode),
      };

      await axios.put(`/api/ai-interviews/${interviewId}`, payload);
    } catch (error) {
      console.error('Error updating interview with questions:', error);
      toast.error('Failed to update interview questions.');
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || formData.selectedTags.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);

      if (formData.questions.length === 0) {
        toast.error('Add at least one question with expected answers.');
        setLoading(false);
        return;
      }

      const structuredQuestions = formData.questions.map((question) => {
        const prompt = (question.prompt || '').trim();
        const type = question.type === 'yes_no' ? 'yes_no' : 'short';
        const expectedPoints = Array.isArray(question.expectedPoints)
          ? question.expectedPoints
            .map((point) => {
              if (typeof point === 'string') return point.trim();
              if (point && typeof point.text === 'string') return point.text.trim();
              return '';
            })
            .filter(Boolean)
          : [];
        const expectedAnswer =
          type === 'yes_no'
            ? (question.expectedAnswer || 'Yes').toLowerCase().startsWith('n')
              ? 'No'
              : 'Yes'
            : (question.expectedAnswer || '').trim();
        const critical = type === 'yes_no' ? Boolean(question.critical) : false;
        const section = typeof question.section === 'string' ? question.section : 'core';
        const allowFollowUps =
          section === 'eligibility' ? false : question.allowFollowUps !== false;
        return {
          prompt,
          type,
          expectedAnswer,
          expectedPoints,
          critical,
          section,
          allowFollowUps,
        };
      });

      const invalidQuestion = structuredQuestions.find((question) => {
        if (!question.prompt) return true;
        if (question.type === 'yes_no') {
          return !question.expectedAnswer;
        }
        return question.expectedPoints.length === 0;
      });

      if (invalidQuestion) {
        toast.error(
          'Each question must include expected answers. Yes/No questions require an expected answer; short answers need expected bullet points.'
        );
        setLoading(false);
        return;
      }

      const effectiveTitle =
        formData.title ||
        (jobPostingDetails?.title ? `${jobPostingDetails.title} AI Interview` : 'AI Interview');
      const payload = {
        title: effectiveTitle,
        role: getInterviewRole(),
        context: formData.context,
        questions: structuredQuestions,
        type: formData.type,
        voiceId: formData.voiceId,
        jobPostingId: formData.jobPostingId,
        deliveryMode: formData.deliveryMode,
        inviteExpiry: formData.inviteExpiry,
        selectedTags: formData.selectedTags,
        eligibility: formData.selectedTags.includes('eligibility')
          ? formData.eligibility
          : undefined,
        aiIntro: formData.aiIntro,
        aiOutro: formData.aiOutro,
        config: {
          personality: formData.personality,
          language: interviewLanguageCode,
        },
        ...deriveAdvancedTypeForLanguage(advancedType, interviewLanguageCode),
      };

      let createdInterviewId = interviewId || '';

      if (interviewId) {
        await axios.put(`/api/ai-interviews/${interviewId}`, payload);
        toast.success('AI Interview updated successfully!');
      } else {
        const response = await axios.post('/api/ai-interviews/create', payload);

        // eslint-disable-next-line no-console
        logger.log('AI Interview created successfully:', response.data);
        const responseInfo = response && response.data ? response.data : {};
        const responseInterview =
          responseInfo && responseInfo.interview ? responseInfo.interview : null;
        const responseDirectId = responseInfo && responseInfo._id ? responseInfo._id : null;
        const responseInterviewId =
          responseInterview && responseInterview._id ? responseInterview._id : null;

        // eslint-disable-next-line no-console
        logger.log('Response structure:', {
          hasInterview: !!responseInterview,
          hasId: !!responseDirectId,
          interviewId: responseInterviewId,
          directId: responseDirectId,
        });

        createdInterviewId = responseInterviewId || responseDirectId || '';
        toast.success('AI Interview created successfully!');
      }

      // Store interview ID in state for later use
      setInterviewId(createdInterviewId);

      // If in modal mode, close modal and optionally refresh parent
      if (isModal && onClose) {
        onClose(createdInterviewId);
        return;
      }

      // Navigate based on context
      if (fromHireLoop) {
        // Navigate to invite page first, then return to HireLoop
        navigate('/dashboard/ai-interviews/' + createdInterviewId + '/invite', {
          state: {
            returnTo: returnTo,
            fromHireLoop: true,
            jobPostingId: linkedJobPostingId,
            screeningChoice: locationState.screeningChoice,
            jobData: locationState.jobData, // Pass jobData through
          },
        });
      } else {
        // Navigate to the invite page
        navigate('/dashboard/ai-interviews/' + createdInterviewId + '/invite');
      }
    } catch (error) {
      console.error('Error creating AI interview:', error);

      const errorResponse = error && error.response ? error.response : null;
      const errorData = errorResponse && errorResponse.data ? errorResponse.data : null;
      if (errorData && errorData.message) {
        toast.error(errorData.message);
      } else {
        toast.error('Failed to create AI interview');
      }
    } finally {
      setLoading(false);
    }
  };

  // Build step 0 rows. When type-selection is enabled the step becomes two phase rows
  // (Interview Type → Choose Method) to mirror the Assessment modal's sidebar pattern.
  const step0Rows = enableTypeSelection
    ? [
      { id: 0, label: 'Interview Type', icon: SparklesIcon, _phase: 'type' },
      {
        id: 0,
        label: interviewCategory === 'advanced' && !advancedMethodPending ? 'Advanced Type' : 'Choose Method',
        icon: SparklesIcon,
        _phase: 'method',
      },
    ]
    : enableCreationModeSelection
      ? [{ id: 0, label: 'Choose Method', icon: SparklesIcon }]
      : [];

  // Step configuration for sidebar (5 steps now with Proctoring)
  const steps = [
    ...(enableCreatorSelection ? [{ id: -1, label: 'Who Creates', icon: EnvelopeIcon }] : []),
    ...(showVisibilityStep ? [{ id: -2, label: 'Access Level', icon: UserGroupIcon }] : []),
    ...step0Rows,
    { id: 1, label: 'Details', icon: ClipboardDocumentListIcon },
    { id: 2, label: 'Edit', icon: EyeIcon },
    { id: 3, label: 'Proctoring', icon: ShieldCheckIcon },
    { id: 4, label: 'Invite', icon: ShareIcon },
    { id: 5, label: 'Demo Preview', icon: PlayIcon },
  ];

  // Check if step is accessible
  const isStepAccessible = (stepId) => {
    if (stepId === -1) return enableCreatorSelection;
    if (stepId === -2) return showVisibilityStep && (creatorChoice === CREATOR_CHOICE.SELF || forceShowVisibilityStep);
    if (stepId === 0) return enableTypeSelection || enableCreationModeSelection;
    if (enableCreatorSelection && creatorChoice !== CREATOR_CHOICE.SELF) return false;
    // When type-selection is on, we also require a category (advanced types are all
    // disabled for now, so in practice only 'standard' advances past step 0).
    if (enableTypeSelection && !interviewCategory) return false;
    if (enableCreationModeSelection && !creationMode) return false;
    if (skipInviteStep && (stepId === 4 || stepId === 5)) return false;
    if (stepId === 1) return true;
    const hasTitle =
      formData.title || (jobPostingDetails?.title ? `${jobPostingDetails.title} AI Interview` : '');
    if (stepId === 2) return !!hasTitle.trim() && formData.selectedTags.length > 0;
    if (stepId === 3) return !isLibraryCreateFlow && !!hasTitle.trim() && formData.selectedTags.length > 0;
    if (stepId === 4) return formData.questions.length > 0; // Invite step
    if (stepId === 5) return formData.questions.length > 0; // Demo step
    return false;
  };

  const displaySteps = isEditLibrary
    ? steps.filter((step) => step.id === 2)
    : skipInviteStep
      ? steps.filter((step) => (isLibraryCreateFlow ? step.id <= 2 : step.id <= 3))
      : steps;

  /** Library access step: require level; if Shared and org has teams, require at least one team. */
  const isAccessLevelChoiceComplete = useMemo(() => {
    if (!visibilityLevel) return false;
    if (visibilityLevel === 'private') return true;
    if (loadingAccessOptions) return false;
    const deptList = accessOptions?.departments;
    if (Array.isArray(deptList) && deptList.length > 0) {
      return selectedSharedDepartmentIds.size > 0;
    }
    return true;
  }, [visibilityLevel, loadingAccessOptions, accessOptions, selectedSharedDepartmentIds]);

  const isExternalLinkStep =
    enableCreatorSelection && creatorChoice === CREATOR_CHOICE.EXTERNAL && currentStep === -3;

  // Check if step is complete
  const isStepComplete = (stepId) => {
    if (stepId === -2) return isAccessLevelChoiceComplete;
    if (stepId === -1) return Boolean(creatorChoice);
    if (stepId === 0) {
      if (enableTypeSelection) {
        // Step 0 is complete when the user has advanced off it (so currentStep >= 1)
        // AND the required selections are in place for the chosen category.
        if (currentStep < 1) return false;
        if (interviewCategory === 'advanced') return Boolean(advancedType) && Boolean(creationMode);
        return Boolean(creationMode);
      }
      return Boolean(creationMode);
    }
    const hasTitle =
      formData.title || (jobPostingDetails?.title ? `${jobPostingDetails.title} AI Interview` : '');
    if (stepId === 1) {
      const hasContext = isContextRequired ? Boolean(contextValue) : true;
      return !!hasTitle.trim() && formData.selectedTags.length > 0 && hasContext;
    }
    if (stepId === 2) return currentStep >= 2;
    if (stepId === 3) return !isLibraryCreateFlow && formData.questions.length > 0;
    if (stepId === 4) return currentStep >= 4; // Invite step is complete only if user has reached it
    if (stepId === 5) return currentStep >= 5; // Demo step is complete only if user has reached it
    return false;
  };

  // Handle sharing actions
  const handleShareAction = (platform) => {
    if (platform === 'email') {
      // Email is the default working option
      return;
    }
    // Show coming soon for other platforms
    setComingSoonFeature(platform);
    setShowComingSoon(true);
  };


  if (isModal || linkedJobPostingId) {
    return (
      <>
      <div
        className={`flex w-full min-h-0 overflow-hidden prevent-body-scroll ${isModal ? 'h-full max-h-full' : 'h-full max-h-[85vh]'}`}
      >
        {/* Inject animation styles */}
        <style>{animationStyles}</style>
        {/* Overwrite expected-answer confirmation (replaces window.confirm for app consistency) */}
        <ConfirmModal
          isOpen={overwriteExpectedConfirm.open}
          onClose={() => setOverwriteExpectedConfirm({ open: false, index: null })}
          onConfirm={() => {
            const idx = overwriteExpectedConfirm.index;
            setOverwriteExpectedConfirm({ open: false, index: null });
            if (typeof idx === 'number') {
              runAutoGenerateExpected(idx);
            }
          }}
          title="Overwrite expected answer?"
          message="This will replace your existing expected answer with a newly generated one. This action cannot be undone."
          confirmLabel="Overwrite"
          cancelLabel="Cancel"
          variant="warning"
        />
        {libraryEditImpactModal}
        {/* Save to Library confirmation modal (AI Interview Library) */}
        {showSaveToLibraryConfirm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/20 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 transition-all duration-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Save to AI Interview Library?
              </h3>
              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                Click Yes to save this interview and its questions for future screenings.
                <br />
                You can find it later under <strong>Library → AI Interview Library</strong> in the
                sidebar.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (savingToLibrary) return;
                    setShowSaveToLibraryConfirm(false);
                    handleStepNavigation(3);
                  }}
                  disabled={savingToLibrary}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 disabled:opacity-50 transition-all duration-200"
                >
                  Continue without saving
                </button>
                <button
                  type="button"
                  onClick={handleSaveToLibrary}
                  disabled={savingToLibrary}
                  data-testid="save-to-library-btn"
                  className="px-4 py-2.5 text-sm font-medium text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] disabled:opacity-50 flex items-center gap-2 transition-all duration-200"
                >
                  {savingToLibrary ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save to library'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Discard confirmation modal for modal usage */}
        {showDiscardConfirm && (
          <div className="fixed inset-0 z-[105] flex items-center justify-center bg-black/20 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 transition-all duration-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Discard interview setup?</h3>
              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                Click Yes to discard this AI interview configuration and close the wizard. Any unsaved
                changes and generated questions will be lost.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowDiscardConfirm(false)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200"
                >
                  No
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDiscardConfirm(false);
                    if (onClose) onClose(null);
                  }}
                  className="px-4 py-2.5 text-sm font-medium text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] transition-all duration-200"
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}
        {showJdUploadModal && shouldShowJdModalCta && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/20" aria-hidden />
            <div className="relative w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Upload Job Description</h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Optional. Add a JD to improve role-specific question generation.
                  </p>
                </div>

                {/* JD modal intentionally only shows JD upload UI */}
                <button
                  type="button"
                  onClick={() => {
                    if (!jdProcessing) setShowJdUploadModal(false);
                  }}
                  disabled={jdProcessing}
                  className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>

              <div
                onDrop={handleJdFileDrop}
                onDragOver={handleJdDragOver}
                onDragLeave={handleJdDragLeave}
                onClick={() => !jdProcessing && jdFileInputRef.current?.click()}
                className={`rounded-xl border-2 border-dashed p-5 transition-all duration-200 ${
                  isDraggingJd
                    ? 'border-[#007BFF] bg-[#007BFF]/5'
                    : jdExtractedSummary
                      ? 'border-emerald-300 bg-emerald-50'
                      : 'border-gray-200 hover:border-[#007BFF]/50 hover:bg-gray-50 cursor-pointer'
                }`}
              >
                <input
                  id="ai-jd-file-input"
                  ref={jdFileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleJdFileChange}
                  className="hidden"
                />
                {jdProcessing ? (
                  <div className="flex flex-col items-center gap-2 text-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#007BFF] border-t-transparent" />
                    <p className="text-sm font-medium text-[#007BFF]">Processing JD...</p>
                    <p className="text-xs text-gray-500">Extracting job details from your file</p>
                  </div>
                ) : jdExtractedSummary ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckIcon className="h-4 w-4" />
                      <p className="text-sm font-semibold">JD processed successfully</p>
                    </div>
                    <p className="text-xs text-gray-700">
                      <span className="font-semibold">Title:</span> {jdExtractedSummary.title || '—'}
                    </p>
                    {jdExtractedSummary.location && (
                      <p className="text-xs text-gray-700">
                        <span className="font-semibold">Location:</span> {jdExtractedSummary.location}
                      </p>
                    )}
                    {jdExtractedSummary.experience && (
                      <p className="text-xs text-gray-700">
                        <span className="font-semibold">Experience:</span>{' '}
                        {jdExtractedSummary.experience}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 text-center">
                    <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-gray-100">
                      <DocumentDuplicateIcon className="h-5 w-5 text-gray-500" />
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      Drop your JD file here or <span className="text-[#007BFF] underline">browse</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      Supports PDF, DOC, DOCX (max {MAX_JD_FILE_SIZE_MB}MB)
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-center justify-end gap-2">
                {jdExtractedSummary ? (
                  <>
                    <button
                      type="button"
                      onClick={() => jdFileInputRef.current?.click()}
                      disabled={jdProcessing}
                      className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-60"
                    >
                      Replace file
                    </button>
                    <button
                      type="button"
                      onClick={handleJdRemoveFile}
                      disabled={jdProcessing}
                      className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-60"
                    >
                      Remove
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  onClick={() => setShowJdUploadModal(false)}
                  disabled={jdProcessing}
                  className="px-3.5 py-1.5 text-xs font-semibold text-white bg-[#007BFF] rounded-lg hover:bg-[#0062CC] disabled:opacity-60"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Sidebar */}
        {!isExternalLinkStep && (
          <div className="w-52 flex-shrink-0 bg-white border-r border-gray-100 flex flex-col">
            {/* Sidebar Header */}
            {/* <div className="p-4 border-b border-gray-100">
              <h1 className="text-base font-semibold text-gray-900">Create Interview</h1>
              {linkedJobPostingId && (
                <p className="mt-1 text-xs text-gray-500 truncate" title={linkedJobPostingId}>
                  
                </p>
              )}
            </div> */}

            {/* Step Navigation */}
            <nav className="flex-1 p-3 space-y-0.5">
              {displaySteps.map((step, index) => {
                const isPhaseStep = Boolean(step._phase);
                const isActive = isPhaseStep
                  ? currentStep === step.id && step0Phase === step._phase
                  : currentStep === step.id && !(step.id === 0 && enableTypeSelection);
                const isComplete = isPhaseStep
                  ? step._phase === 'type'
                    ? Boolean(interviewCategory)
                    : interviewCategory === 'advanced'
                      ? Boolean(advancedType)
                      : Boolean(creationMode)
                  : isStepComplete(step.id);
                const isAccessible = isPhaseStep
                  ? step._phase === 'type'
                    ? true
                    : Boolean(interviewCategory)
                  : isStepAccessible(step.id);

                const handleClick = () => {
                  if (isPhaseStep) {
                    if (!isAccessible) return;
                    if (step._phase === 'type') {
                      setInterviewCategory(null);
                      setCreationMode(null);
                      setAdvancedType(null);
                      setAdvancedMethodPending(false);
                      setInterviewLanguageCode('en');
                      setCurrentStep(0);
                    } else {
                      setCreationMode(null);
                      setAdvancedType(null);
                      setAdvancedMethodPending(false);
                      setInterviewLanguageCode('en');
                      setCurrentStep(0);
                    }
                    return;
                  }
                  handleStepNavigation(step.id);
                };

                return (
                  <button
                    key={`${step.id}-${step._phase || ''}`}
                    type="button"
                    onClick={handleClick}
                    disabled={!isAccessible && step.id > currentStep}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-md text-left transition-all ${isActive
                        ? 'bg-[#007BFF]/10 text-[#007BFF]'
                        : isComplete
                          ? 'text-gray-700 hover:bg-gray-50'
                          : isAccessible || step.id < currentStep
                            ? 'text-gray-600 hover:bg-gray-50'
                            : 'text-gray-400 cursor-not-allowed'
                      }`}
                  >
                    <div
                      className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all ${isActive
                          ? 'bg-[#007BFF] text-white'
                          : isComplete
                            ? 'bg-[#007BFF] text-white'
                            : 'bg-gray-200 text-gray-500'
                        }`}
                    >
                      {isComplete && !isActive ? (
                        <CheckIcon className="w-3.5 h-3.5" />
                      ) : (
                        <span className="text-xs font-medium">{index + 1}</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-sm font-medium truncate ${isActive ? 'text-[#007BFF]' : ''}`}
                      >
                        {step.label}
                      </p>
                    </div>
                  </button>
                );
              })}
            </nav>

            {/* Sidebar Footer (no extra Cancel button for modal; header X handles close) */}
            {!isModal && (
              <div className="p-3 border-t border-gray-100">
                <button
                  onClick={() => onClose && onClose(null)}
                  className="w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors flex items-center justify-center gap-2"
                >
                  <XMarkIcon className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-white overflow-hidden">
          {/* Content Header with top-right close in modal usage */}
          <div className="flex-shrink-0 px-6 py-3 border-b border-gray-100 bg-white relative">
            {isModal && (isLibraryCreateFlow || isEditLibrary) && (
              <button
                type="button"
                onClick={() => {
                  if (isLibraryCreateFlow) {
                    setShowDiscardConfirm(true);
                  } else if (onClose) {
                    onClose(null);
                  }
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                aria-label="Close"
                data-testid="close-modal-btn"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
            {currentStep === 2 && advancedType !== ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED ? (
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {editLibraryLoading
                      ? 'Loading interview flow...'
                      : `Interview Flow Edit · ${formData.questions.length} ${
                          formData.questions.length === 1 ? 'question' : 'questions'
                        }`}
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    {editLibraryLoading
                      ? 'Fetching interview details...'
                      : 'Click a question to expand and edit. Drag to Reorder'}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleAddQuestion}
                    disabled={
                      editLibraryLoading ||
                      pendingQuestionLocalId !== null ||
                      formData.questions.length >= MAX_INTERVIEW_QUESTIONS_TOTAL
                    }
                    aria-disabled={
                      editLibraryLoading ||
                      pendingQuestionLocalId !== null ||
                      formData.questions.length >= MAX_INTERVIEW_QUESTIONS_TOTAL
                    }
                    title={
                      formData.questions.length >= MAX_INTERVIEW_QUESTIONS_TOTAL
                        ? `You can add at most ${MAX_INTERVIEW_QUESTIONS_TOTAL} questions`
                        : undefined
                    }
                    className="px-2 py-1 border border-gray-200 text-gray-700 rounded-md hover:bg-gray-50 flex items-center gap-1 text-sm font-medium transition-all disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                    aria-label="Add question"
                    data-testid="add-question-btn"
                  >
                    <PlusIcon className="w-4 h-4" />
                    <span>Add Question</span>
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-lg font-semibold text-gray-900">
                  {currentStep === -1 && 'Who will create this interview?'}
                  {currentStep === -2 && showVisibilityStep && 'Configure access level'}
                  {currentStep === -3 && 'Send an external creation link'}
                  {currentStep === 0 &&
                    (step0Phase === 'type'
                      ? 'Choose Interview Type'
                      : interviewCategory === 'advanced'
                        ? 'Choose Advanced Interview Type'
                        : 'Choose Creation Method')}
                  {currentStep === 1 && 'Interview Details'}
                  {currentStep === 2 && (advancedType === ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED ? 'Question Modules' : 'Interview Flow Edit')}
                  {currentStep === 3 && 'Proctoring Settings'}
                  {currentStep === 4 && 'Invite Candidates'}
                  {currentStep === 5 && 'Demo Preview'}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {currentStep === -2 && showVisibilityStep &&
                    'Select whether this library interview should be shared with your team or kept private.'}
                  {currentStep === -1 &&
                    'Choose whether you want to create the interview yourself, or generate a link to let someone else create it for you.'}
                  {currentStep === -3 &&
                    'Generate and share this link. The recipient can open it and create the interview in their browser.'}
                  {currentStep === 0 &&
                    (step0Phase === 'type'
                      ? 'Select the type of interview you want to create.'
                      : interviewCategory === 'advanced'
                        ? 'Pick a specialized interview format. More types are coming soon.'
                        : 'Select how you want to create your interview questions: with AI or manually from scratch.')}
                  {currentStep === 1 &&
                    (creationMode === CREATION_MODE.MANUAL
                      ? 'Set the interview details. You will add and structure questions manually in the next step.'
                      : 'Set the categories, number of questions, and context below. Our AI generates the interview so you get a ready-to-use flow without writing every question.')}
                  {currentStep === 2 && (advancedType === ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED ? 'Upload a question bank file or build modules manually.' : 'Review and edit questions for your interview.')}
                  {currentStep === 3 &&
                    'Configure proctoring settings to ensure interview integrity and detect suspicious behavior during the interview.'}
                  {currentStep === 4 && 'Share interview link via email or other platforms'}
                  {currentStep === 5 && 'See how candidates will experience the interview'}
                </p>
              </>
            )}
          </div>

          {/* Scrollable Content */}
          <div
            className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-3 overscroll-contain scroll-smooth"
            style={{
              minHeight: 0,
              maxHeight: '100%',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
              position: 'relative'
            }}
          >
            {currentStep === -1 && enableCreatorSelection && (
              <div className="h-full flex flex-col">
                <div className="flex flex-col items-center justify-center py-8 px-4 h-full">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl w-full items-stretch">
                    <button
                      type="button"
                      data-testid="self-create-btn"
                      onClick={() => {
                        setCreatorChoice(CREATOR_CHOICE.SELF);
                        setCurrentStep(
                          showVisibilityStep
                            ? -2
                            : enableTypeSelection || enableCreationModeSelection
                              ? 0
                              : 1
                        );
                      }}
                      className="group relative p-6 bg-white border-2 border-gray-200 rounded-2xl hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 text-left h-full"
                    >
                      <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-[#007BFF]/10 to-[#007BFF]/5 mb-4 group-hover:from-[#007BFF]/20 group-hover:to-[#007BFF]/10 transition-colors">
                        <ClipboardDocumentListIcon className="w-7 h-7 text-[#007BFF]" />
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">I&apos;ll create it</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Continue to choose AI vs manual creation and build the interview here.
                      </p>
                      <ul className="space-y-2.5 text-sm text-gray-500">
                        <li className="flex items-center gap-2">
                          <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>Create now inside TalentCrux</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>Use AI generation or build from scratch</span>
                        </li>
                      </ul>
                    </button>

                    <button
                      type="button"
                      data-testid="external-create-btn"
                      onClick={() => {
                        setCreatorChoice(CREATOR_CHOICE.EXTERNAL);
                        setCurrentStep(-3);
                      }}
                      className="group relative p-6 bg-white border-2 border-gray-200 rounded-2xl hover:border-[#007BFF] hover:shadow-lg transition-all duration-300 text-left h-full"
                    >
                      <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-gray-100 mb-4 group-hover:bg-[#007BFF]/10 transition-colors">
                        <LinkIcon className="w-7 h-7 text-gray-600 group-hover:text-[#007BFF]" />
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Send a link to someone</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Generate a secure link so another person can create the interview for you.
                      </p>
                      <ul className="space-y-2.5 text-sm text-gray-500">
                        <li className="flex items-center gap-2">
                          <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>They can complete creation in a separate tab</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>You can share via email/Slack etc.</span>
                        </li>
                      </ul>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {currentStep === -3 &&
              enableCreatorSelection &&
              creatorChoice === CREATOR_CHOICE.EXTERNAL &&
              !hideExternalCreationLink && (
                <div className="h-full flex flex-col items-center justify-center py-10 px-4">
                  <div className="w-full max-w-2xl">
                    <div className="rounded-2xl border border-gray-200 bg-gradient-to-b from-white to-gray-50 shadow-sm overflow-hidden">
                      <div className="p-6 border-b border-gray-100">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="inline-flex items-center gap-2 mb-2">
                              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#007BFF]/10">
                                <LinkIcon className="w-5 h-5 text-[#007BFF]" />
                              </span>
                              <h3 className="text-lg font-semibold text-gray-900">External Creation Link</h3>
                            </div>
                            <p className="text-sm text-gray-600">
                              Share this link with your teammate. They can open it and create the interview for you.
                            </p>
                            <p className="text-xs text-gray-500 mt-2">
                              Tip: share the link privately (direct message or email).
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setCreatorChoice(null);
                              setExternalCreationLink('');
                              setCurrentStep(-1);
                            }}
                            className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                          >
                            Back
                          </button>
                        </div>
                      </div>

                      <div className="p-6 space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                          <button
                            type="button"
                            onClick={handleGenerateExternalCreationLink}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] transition-colors"
                          >
                            <LinkIcon className="w-4 h-4" />
                            Generate link
                          </button>
                          <div className="text-xs text-gray-500">
                            {externalCreationLink ? 'Link ready to share.' : 'Generate a link to continue.'}
                          </div>
                        </div>

                        {externalCreationLink && (
                          <div className="rounded-xl border border-gray-200 bg-white p-4">
                            <label className="block text-xs font-semibold text-gray-700 mb-2">
                              Shareable URL
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                readOnly
                                value={externalCreationLink}
                                className="flex-1 min-w-0 px-3 py-2.5 text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none"
                              />
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!externalCreationLink) return;
                                  try {
                                    if (navigator?.clipboard?.writeText) {
                                      await navigator.clipboard.writeText(externalCreationLink);
                                    }
                                    setExternalLinkCopied(true);
                                    setTimeout(() => setExternalLinkCopied(false), 2000);
                                    toast.success('Link copied.');
                                  } catch (_) {
                                    toast.error('Could not copy link. Please copy manually.');
                                  }
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-800 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                              >
                                <DocumentDuplicateIcon className="w-5 h-5" />
                                {externalLinkCopied ? 'Copied' : 'Copy'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {currentStep === -2 && showVisibilityStep && (
              <div className="h-full flex flex-col items-center justify-center py-10 px-4">
                <div className="w-full max-w-2xl">
                  <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-gray-100 bg-gray-50/80">
                      <h3 className="text-lg font-semibold text-gray-900">Access Level</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Configure who can see this library AI interview.
                      </p>
                    </div>
                    <div className="p-6 space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Access Level (required)
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <label
                            className={`relative flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              visibilityLevel === 'shared'
                                ? 'border-[#007BFF] bg-[#007BFF]/5 shadow-sm'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            <input
                              type="radio"
                              name="visibilityLevel"
                              value="shared"
                              checked={visibilityLevel === 'shared'}
                              onChange={() => setVisibilityLevel('shared')}
                              className="mt-1 w-4 h-4 text-[#007BFF] border-gray-300 focus:ring-[#007BFF]"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <UserGroupIcon className="h-5 w-5 text-[#007BFF] flex-shrink-0" />
                                <span className="font-medium text-gray-900">Shared</span>
                              </div>
                              <p className="text-sm text-gray-500 mt-1">
                                Select which teams can see this interview
                              </p>
                            </div>
                          </label>

                          <label
                            className={`relative flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              visibilityLevel === 'private'
                                ? 'border-[#007BFF] bg-[#007BFF]/5 shadow-sm'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            <input
                              type="radio"
                              name="visibilityLevel"
                              value="private"
                              checked={visibilityLevel === 'private'}
                              onChange={() => setVisibilityLevel('private')}
                              className="mt-1 w-4 h-4 text-[#007BFF] border-gray-300 focus:ring-[#007BFF]"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <LockClosedIcon className="h-5 w-5 text-[#007BFF] flex-shrink-0" />
                                <span className="font-medium text-gray-900">Private</span>
                              </div>
                              <p className="text-sm text-gray-500 mt-1">Only you can see this interview</p>
                            </div>
                          </label>
                        </div>
                      </div>

                      {visibilityLevel === 'shared' && (
                        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                          <div className="px-5 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                            <h4 className="text-sm font-semibold text-gray-900">Shared with teams</h4>
                            <p className="text-xs text-gray-500 mt-1">
                              Select one or more teams. Only those teams can see this interview in the library.
                            </p>
                          </div>
                          <div className="p-4 max-h-[260px] overflow-y-auto">
                            {loadingAccessOptions || !Array.isArray(accessOptions?.departments) ? (
                              <div className="flex flex-col items-center justify-center py-10 gap-3">
                                <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#007BFF] border-t-transparent" />
                                <span className="text-sm text-gray-500">Loading teams…</span>
                              </div>
                            ) : accessOptions.departments.length === 0 ? (
                              <div className="text-center py-8 px-4 rounded-lg bg-gray-50 border border-gray-100">
                                <BuildingOffice2Icon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                                <p className="text-sm text-gray-500">
                                  No teams yet. Create teams in Organization to share interviews.
                                </p>
                              </div>
                            ) : (
                              <div className="grid gap-2">
                                {accessOptions.departments.map((dept) => (
                                  <label
                                    key={dept._id}
                                    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                      selectedSharedDepartmentIds.has(dept._id)
                                        ? 'border-[#007BFF] bg-[#007BFF]/5 shadow-sm'
                                        : 'border-gray-100 bg-gray-50/50 hover:border-gray-200 hover:bg-gray-50'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedSharedDepartmentIds.has(dept._id)}
                                      onChange={() => toggleSharedDepartment(dept._id)}
                                      className="sr-only"
                                    />
                                    <div
                                      className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                        selectedSharedDepartmentIds.has(dept._id)
                                          ? 'border-[#007BFF] bg-[#007BFF]'
                                          : 'border-gray-300 bg-white'
                                      }`}
                                    >
                                      {selectedSharedDepartmentIds.has(dept._id) && (
                                        <svg
                                          className="w-3 h-3 text-white"
                                          fill="currentColor"
                                          viewBox="0 0 20 20"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      )}
                                    </div>
                                    <BuildingOffice2Icon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                                    <span className="text-sm font-medium text-gray-900">{dept.name}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setCreatorChoice(null);
                            setCurrentStep(-1);
                          }}
                          className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors"
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          onClick={() => setCurrentStep(enableTypeSelection || enableCreationModeSelection ? 0 : 1)}
                          disabled={!isAccessLevelChoiceComplete}
                          className="px-4 py-2.5 text-sm font-semibold text-white bg-[#007BFF] rounded-xl hover:bg-[#0062CC] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                        >
                          Continue
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 0 && enableTypeSelection && step0Phase === 'type' && (
              <div className="h-full flex flex-col">
                <InterviewCategoryStep
                  onSelectCategory={(category) => {
                    setInterviewCategory(category);
                    // Reset downstream selections so the sidebar reflects a fresh method phase
                    setCreationMode(null);
                    setAdvancedType(null);
                    setAdvancedMethodPending(false);
                    setInterviewLanguageCode('en');
                  }}
                />
              </div>
            )}

            {currentStep === 0 &&
              enableTypeSelection &&
              step0Phase === 'method' &&
              interviewCategory === 'advanced' &&
              !advancedMethodPending && (
                <div className="h-full flex flex-col">
                  <AdvancedTypePickerStep
                    onSelectAdvancedType={(type) => {
                      if (type === ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED) {
                        // Rubric-based has its own dedicated creation flow
                        setAdvancedType(type);
                        setCreationMode(CREATION_MODE.AI);
                        handleStepNavigation(1);
                        return;
                      }
                      setAdvancedType(type);
                      setCreationMode(null);
                      // Language is chosen in the Details step (English default);
                      // an explicit advanced type doesn't pin a language.
                      setInterviewLanguageCode('en');
                      // Types that support a method choice show Choose Method next;
                      // all other advanced types go straight to Details (AI-only).
                      if (ADVANCED_TYPES_WITH_METHOD_CHOICE.has(type)) {
                        setAdvancedMethodPending(true);
                      } else {
                        setCreationMode(CREATION_MODE.AI);
                        setAdvancedMethodPending(false);
                        setCurrentStep(1);
                      }
                    }}
                  />
                </div>
              )}

            {currentStep === 0 &&
              enableTypeSelection &&
              step0Phase === 'method' &&
              interviewCategory === 'advanced' &&
              advancedMethodPending && (
                <div className="h-full flex flex-col">
                  <CreationModeStep
                    onSelectMode={(mode) => {
                      setCreationMode(mode);
                      setAdvancedMethodPending(false);
                      setCurrentStep(1);
                    }}
                  />
                </div>
              )}

            {currentStep === 0 &&
              ((enableTypeSelection &&
                step0Phase === 'method' &&
                interviewCategory === 'standard') ||
                (!enableTypeSelection && enableCreationModeSelection)) && (
                <div className="h-full flex flex-col">
                  <CreationModeStep
                    onSelectMode={(mode) => {
                      setCreationMode(mode);
                      setCurrentStep(1);
                    }}
                  />
                  {!hideExternalCreationLink && !enableCreatorSelection && (
                    <div className="max-w-2xl w-full mx-auto px-4 pb-6">
                      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900">External Creation Link</p>
                            <p className="text-xs text-gray-600 mt-1">
                              Generate a link to open this interview creation modal quickly.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={handleGenerateExternalCreationLink}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-[#007BFF] bg-white border border-[#007BFF]/30 rounded-lg hover:bg-[#007BFF]/5 transition-colors whitespace-nowrap"
                          >
                            <LinkIcon className="w-4 h-4" />
                            Generate Link
                          </button>
                        </div>

                        {externalCreationLink && (
                          <div className="mt-3 flex items-center gap-2">
                            <input
                              type="text"
                              readOnly
                              value={externalCreationLink}
                              className="flex-1 min-w-0 px-3 py-2 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg focus:outline-none"
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                if (!externalCreationLink) return;
                                try {
                                  if (navigator?.clipboard?.writeText) {
                                    await navigator.clipboard.writeText(externalCreationLink);
                                  }
                                  setExternalLinkCopied(true);
                                  setTimeout(() => setExternalLinkCopied(false), 2000);
                                  toast.success('Link copied.');
                                } catch (_) {
                                  toast.error('Could not copy link. Please copy manually.');
                                }
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <DocumentDuplicateIcon className="w-4 h-4" />
                              {externalLinkCopied ? 'Copied' : 'Copy'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

            {currentStep === 1 && (
              <div className="space-y-5 max-w-4xl">
                {/* Title + Category — compact row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                  <div className="flex flex-col">
                    <label htmlFor="title" className="block text-sm font-semibold text-gray-800 mb-1.5">
                      Interview title <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Give your interview a clear name to help you identify it later.
                    </p>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      data-testid="interview-title-input"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/25 text-sm"
                      placeholder="Enter Interview Title"
                      maxLength={100}
                      required
                    />
                    <p className="mt-1 text-[10px] text-gray-400">{formData.title.length}/100 characters</p>
                  </div>

                  <div className="relative flex flex-col" ref={categoryDropdownRef}>
                    <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Choose the closest role family so we can tailor questions to that track.
                    </p>
                    <button
                      type="button"
                      data-testid="category-select-btn"
                      onClick={() => setIsCategoryOpen((open) => !open)}
                      className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm hover:border-[#007BFF]/60 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30"
                    >
                      <span className={formData.category ? '' : 'text-gray-400'}>
                        {formData.category
                          ? interviewCategoryOptions.find((c) => c.value === formData.category)
                            ?.label || 'Select category'
                          : 'Select category'}
                      </span>
                      <span
                        className={`ml-2 transform text-gray-400 transition-transform duration-150 ${isCategoryOpen ? 'rotate-180' : 'rotate-0'
                          }`}
                      >
                        ▾
                      </span>
                    </button>
                    {isCategoryOpen && (
                      <div className="absolute z-20 top-[calc(100%+0.25rem)] w-full min-w-[min(100%,20rem)] origin-top rounded-lg border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 transition-all duration-150 ease-out">
                        <div className="max-h-64 overflow-y-auto py-1">
                          {interviewCategoryOptions.map((option) => {
                            const selected = formData.category === option.value;
                            return (
                              <button
                                key={option.value}
                                type="button"
                                data-testid={`category-option-${option.value.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                onClick={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    category: prev.category === option.value ? '' : option.value,
                                  }));
                                  setIsCategoryOpen(false);
                                }}
                                className={`flex w-full flex-col items-start px-3 py-2 text-left text-sm transition-colors ${selected
                                    ? 'bg-[#007BFF]/5 text-gray-900'
                                    : 'hover:bg-gray-50 text-gray-800'
                                  }`}
                              >
                                <span className="font-medium">{option.label}</span>
                                <span className="mt-0.5 text-xs text-gray-500">
                                  {option.description}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Interview Category — chip style like assessment (hidden for manual in library flow and rubric-based) */}
                {!(creationMode === CREATION_MODE.MANUAL && isLibraryCreateFlow) && advancedType !== ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                      Interview Category <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Select one or more to steer AI question generation (e.g. technical, behavioral).
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {questionTags.map((tag) => {
                        const isSelected = formData.selectedTags.includes(tag.id);
                        return (
                          <button
                            key={tag.id}
                            type="button"
                            data-testid={`tag-${tag.id}`}
                            onClick={() => toggleTag(tag.id)}
                            className={`
                            inline-flex items-center px-3 py-2 rounded-md text-sm font-medium
                            transition-all duration-200 select-none
                            ${isSelected
                                ? 'bg-[#007BFF] text-white border border-[#007BFF] shadow-sm shadow-[#007BFF]/15'
                                : 'bg-white text-gray-700 border border-gray-300 hover:border-[#007BFF]/60 hover:bg-[#007BFF]/5'
                              }
                          `}
                          >
                            {tag.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Interview Mode (collapsible) + Interview language row; JD via Context “Upload JD” */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                    <div
                      className="relative min-w-0"
                      ref={interviewModeDropdownRef}
                    >
                      <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                        Interview Mode <span className="text-red-500">*</span>
                      </label>
                      <p className="text-xs text-gray-500 mb-2 min-h-[2rem]">
                        Choose how candidates will take this interview. All interviews are automatically recorded.
                      </p>
                      {(() => {
                        const modeOptions = [
                          {
                            id: 'voice',
                            label: 'Voice Interview',
                            description: 'Audio-only with recording',
                            Icon: MicrophoneIcon,
                          },
                          {
                            id: 'video',
                            label: 'Video Interview',
                            description: 'Camera + audio with recording',
                            Icon: VideoCameraIcon,
                          },
                        ];
                        const selectedMode =
                          modeOptions.find((m) => m.id === formData.type) || modeOptions[0];
                        const SelectedIcon = selectedMode.Icon;
                        return (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setInterviewModeDropdownOpen((o) => !o);
                                setLangDropdownOpen(false);
                                setVoiceDropdownOpen(false);
                              }}
                              className={`flex w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-left text-sm shadow-sm transition-all duration-200 ${interviewModeDropdownOpen
                                  ? 'border-[#007BFF] ring-2 ring-[#007BFF]/25'
                                  : 'border-gray-300 hover:border-[#007BFF]/60 hover:bg-gray-50'
                                }`}
                            >
                              <div className="flex min-w-0 items-center gap-2">
                                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-[#007BFF]/10 text-[#007BFF]">
                                  <SelectedIcon className="h-4 w-4" />
                                </div>
                                <div className="min-w-0">
                                  <p className="truncate font-semibold text-gray-900">{selectedMode.label}</p>
                                  <p className="truncate text-[11px] text-gray-500">{selectedMode.description}</p>
                                </div>
                              </div>
                              <ChevronDownIcon
                                className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${interviewModeDropdownOpen ? 'rotate-180' : ''
                                  }`}
                              />
                            </button>
                            {interviewModeDropdownOpen && (
                              <div className="absolute left-0 right-0 z-40 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-xl ring-1 ring-black/5">
                                {modeOptions.map(({ id, label, description, Icon }) => {
                                  const isSelected = formData.type === id;
                                  return (
                                    <button
                                      key={id}
                                      type="button"
                                      onClick={() => {
                                        setFormData((prev) => ({ ...prev, type: id }));
                                        setInterviewModeDropdownOpen(false);
                                      }}
                                      className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${isSelected
                                          ? 'bg-[#007BFF]/5 text-gray-900'
                                          : 'text-gray-800 hover:bg-gray-50'
                                        }`}
                                    >
                                      <div
                                        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md ${isSelected ? 'bg-[#007BFF] text-white' : 'bg-gray-100 text-gray-500'
                                          }`}
                                      >
                                        <Icon className="h-4 w-4" />
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p
                                          className={`font-semibold ${isSelected ? 'text-[#007BFF]' : 'text-gray-900'
                                            }`}
                                        >
                                          {label}
                                        </p>
                                        <p className="text-xs text-gray-500">{description}</p>
                                      </div>
                                      {isSelected ? (
                                        <CheckIcon className="h-5 w-5 flex-shrink-0 text-[#007BFF]" />
                                      ) : null}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    {/* Language selector — always available in the Details step */}
                    <div className="relative min-w-0" ref={langDropdownRef}>
                          <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                            Interview language
                          </label>
                          <p className="text-xs text-gray-500 mb-2 min-h-[2rem]">
                            Choose the language the interviewer will speak.
                          </p>
                          {(() => {
                            const langOptions = LANGUAGE_OPTIONS;
                            const selectedLang = langOptions.find((l) => l.id === interviewLanguageCode);

                            return (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setLangDropdownOpen((o) => !o);
                                    setInterviewModeDropdownOpen(false);
                                    setVoiceDropdownOpen(false);
                                  }}
                                  className={`flex w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-left text-sm shadow-sm transition-all duration-200 ${langDropdownOpen
                                      ? 'border-[#007BFF] ring-2 ring-[#007BFF]/25'
                                      : 'border-gray-300 hover:border-[#007BFF]/60 hover:bg-gray-50'
                                    }`}
                                >
                                  <div className="flex min-w-0 items-center gap-2">
                                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-[#007BFF] to-[#0056b3] text-white shadow-sm">
                                      <span className="text-[10px] font-bold leading-none">{selectedLang?.icon || 'हि'}</span>
                                    </div>
                                    <div className="min-w-0">
                                      <p
                                        className={`truncate font-semibold ${selectedLang ? 'text-gray-900' : 'text-gray-400'
                                          }`}
                                      >
                                        {selectedLang ? selectedLang.name : 'Select language…'}
                                      </p>
                                      <p className="truncate text-[11px] text-gray-500">
                                        {selectedLang ? selectedLang.desc : 'Select a language'}
                                      </p>
                                    </div>
                                  </div>
                                  <ChevronDownIcon
                                    className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${langDropdownOpen ? 'rotate-180' : ''
                                      }`}
                                  />
                                </button>

                                {langDropdownOpen && (
                                  <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-xl max-h-64 overflow-y-auto">
                                    {langOptions.map(({ id, name, icon }) => {
                                      const isSelected = interviewLanguageCode === id;
                                      return (
                                        <div
                                          key={id}
                                          className={`flex cursor-pointer items-center gap-2.5 px-3 py-2.5 transition-all ${isSelected
                                              ? 'border-l-3 border-l-[#007BFF] bg-[#007BFF]/5'
                                              : 'border-l-3 border-l-transparent hover:bg-gray-50'
                                            }`}
                                          onClick={() => {
                                            setInterviewLanguageCode(id);
                                            setLangDropdownOpen(false);
                                          }}
                                        >
                                          <div
                                            className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${isSelected
                                                ? 'bg-gradient-to-br from-[#007BFF] to-[#0056b3] text-white shadow-sm'
                                                : 'bg-gray-100 text-gray-500'
                                              }`}
                                          >
                                            <span className="text-xs font-bold leading-none">{icon}</span>
                                          </div>
                                          <span
                                            className={`text-sm font-semibold ${isSelected ? 'text-[#007BFF]' : 'text-gray-900'
                                              }`}
                                          >
                                            {name}
                                          </span>
                                          {isSelected && (
                                            <CheckIcon className="ml-auto h-5 w-5 flex-shrink-0 text-[#007BFF]" />
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                  </div>


                {/* AI Voice always available; Number of Questions stays AI-only */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  {/* AI Voice Selection */}
                  <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                        AI Voice
                      </label>
                      <p className="text-xs text-gray-500 mb-1.5">
                        {['hi', 'hi-en', 'en-hi', 'kn', 'ta', 'bn', 'te', 'mr', 'gu', 'ml', 'pa', 'tl', 'fr'].includes(interviewLanguageCode)
                          ? `Choose a native voice for the AI interviewer.`
                          : 'Choose the voice accent for the AI interviewer.'}
                      </p>
                      <div className="relative" ref={voiceDropdownRef}>
                        {(() => {
                          const voiceOptions = VOICE_OPTIONS_BY_LANGUAGE[interviewLanguageCode] || englishVoiceOptions;
                          const selectedVoice = voiceOptions.find(v => v.id === formData.voiceId) || voiceOptions[0];

                          return (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setVoiceDropdownOpen((o) => !o);
                                  setInterviewModeDropdownOpen(false);
                                  setLangDropdownOpen(false);
                                }}
                                className={`flex w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-left text-sm shadow-sm transition-all duration-200 ${voiceDropdownOpen
                                    ? 'border-[#007BFF] ring-2 ring-[#007BFF]/25'
                                    : 'border-gray-300 hover:border-[#007BFF]/60 hover:bg-gray-50'
                                  }`}
                              >
                                <div className="flex min-w-0 items-center gap-2">
                                  <div
                                    className="flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-md bg-[#007BFF]/10 text-[#007BFF] transition-colors hover:bg-[#007BFF]/20"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleVoicePreview(selectedVoice.id, selectedVoice.preview);
                                    }}
                                    title="Preview voice"
                                  >
                                    {playingVoiceId === selectedVoice.id ? (
                                      <PauseIcon className="h-4 w-4" />
                                    ) : (
                                      <PlayIcon className="h-4 w-4" />
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="truncate font-semibold text-gray-900">
                                      {selectedVoice.gender}
                                      <span className="font-medium text-gray-400"> · </span>
                                      <span className="font-medium text-gray-700">{selectedVoice.accent}</span>
                                    </p>
                                    <p className="truncate text-left text-[11px] text-gray-500">
                                      {selectedVoice.tone}
                                    </p>
                                  </div>
                                </div>
                                <ChevronDownIcon
                                  className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${voiceDropdownOpen ? 'rotate-180' : ''}`}
                                />
                              </button>

                              {voiceDropdownOpen && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                                  {voiceOptions.map(({ id, gender, accent, tone, preview }) => {
                                    const isSelected = formData.voiceId === id;
                                    return (
                                      <div
                                        key={id}
                                        className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-all ${isSelected
                                            ? 'bg-[#007BFF]/5 border-l-3 border-l-[#007BFF]'
                                            : 'hover:bg-gray-50 border-l-3 border-l-transparent'
                                          }`}
                                        onClick={() => {
                                          setFormData((prev) => ({ ...prev, voiceId: id }));
                                          setVoiceDropdownOpen(false);
                                        }}
                                      >
                                        <div
                                          className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all ${playingVoiceId === id
                                              ? 'bg-gradient-to-br from-[#007BFF] to-[#0056b3] text-white shadow-sm'
                                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                            }`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleVoicePreview(id, preview);
                                          }}
                                          title="Preview voice"
                                        >
                                          {playingVoiceId === id ? (
                                            <PauseIcon className="w-4 h-4" />
                                          ) : (
                                            <PlayIcon className="w-4 h-4" />
                                          )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2">
                                            <span className={`text-sm font-semibold ${isSelected ? 'text-[#007BFF]' : 'text-gray-900'}`}>{gender}</span>
                                            <span className="text-gray-300">•</span>
                                            <span className={`text-sm font-medium ${isSelected ? 'text-[#007BFF]' : 'text-gray-700'}`}>{accent}</span>
                                          </div>
                                          <p className="text-xs text-gray-500">{tone}</p>
                                        </div>
                                        {isSelected && (
                                          <CheckIcon className="w-5 h-5 text-[#007BFF] flex-shrink-0" />
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      <p className="text-xs text-gray-500 mt-1.5">
                        Click the play button to preview.
                      </p>
                  </div>

                  {/* Number of Questions (AI-only, hidden for rubric-based) */}
                  {!isManualLibraryFlow && advancedType !== ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED && (
                    <div>
                      <label
                        htmlFor="maxQuestions"
                        className="block text-sm font-semibold text-gray-800 mb-1.5"
                      >
                        Number of Questions (max 30) <span className="text-red-500" aria-label="required">*</span>
                      </label>
                      <p className="text-xs text-gray-500 mb-1.5">
                        Number of questions to be AI-generated.
                      </p>
                      <input
                        type="number"
                        id="maxQuestions"
                        name="maxQuestions"
                        value={formData.maxQuestions === '' ? '' : String(formData.maxQuestions)}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setFormData((prev) => ({ ...prev, maxQuestions: '' }));
                            return;
                          }
                          const value = Number(raw);
                          if (Number.isNaN(value)) return;
                          setFormData((prev) => ({ ...prev, maxQuestions: value }));
                        }}
                        onWheel={(e) => e.target.blur()}
                        onBlur={() => {
                          setFormData((prev) => {
                            let v = prev.maxQuestions;
                            if (v === '' || v == null || Number.isNaN(Number(v))) v = 1;
                            v = Math.floor(Number(v));
                            if (v < 1) v = 1;
                            if (v > 30) v = 30;
                            return { ...prev, maxQuestions: v };
                          });
                        }}
                        min="1"
                        max="30"
                        className="w-full h-[52px] px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] transition-all text-sm"
                        placeholder="e.g., 6"
                      />

                    </div>
                  )}
                </div>

                {creationMode === CREATION_MODE.MANUAL && isLibraryCreateFlow && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                      Upload Question Bank
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                     Upload your questions. We&apos;ll refine them and generate scoring-ready answers.
                    </p>
                    <div
                      onDrop={handleQuestionFileDrop}
                      onDragOver={handleQuestionFileDragOver}
                      onDragLeave={handleQuestionFileDragLeave}
                      onClick={() =>
                        questionFileInputRef.current?.click()
                      }
                      className={`
                        relative border-2 border-dashed rounded-xl p-5 text-center cursor-pointer
                        transition-all duration-200
                        ${
                          isDraggingQuestionFile
                            ? 'border-[#007BFF] bg-[#007BFF]/5'
                            : questionFile
                              ? 'border-emerald-300 bg-emerald-50'
                              : 'border-gray-200 hover:border-[#007BFF]/50 hover:bg-gray-50'
                        }
                      `}
                    >
                      <input
                        id="ai-question-file-input"
                        ref={questionFileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.txt,.csv"
                        onChange={handleQuestionFileChange}
                        className="hidden"
                      />
                      {questionFile ? (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-emerald-700">Question file ready</p>
                          <p className="text-xs text-gray-700">
                            {questionFile.name} ({(questionFile.size / 1024).toFixed(1)} KB)
                          </p>
                          <p className="text-[11px] text-gray-500">
                            We&apos;ll process this file when you click Create questions.
                          </p>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuestionFileRemove();
                            }}
                            className="mt-1 inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-50"
                          >
                            Remove file
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-gray-50">
                            <DocumentDuplicateIcon className="h-5 w-5 text-gray-400" />
                          </div>
                          <p className="text-sm font-medium text-gray-900">
                            Drop question file here or <span className="text-[#007BFF] underline">browse</span>
                          </p>
                          <p className="text-xs text-gray-500">
                            Supports PDF, DOC, DOCX, TXT, CSV (max {MAX_QUESTION_FILE_SIZE_MB}MB)
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}


                {/* Context (AI-only, hidden for manual in library flow and rubric-based) */}
                {!(creationMode === CREATION_MODE.MANUAL && isLibraryCreateFlow) && advancedType !== ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED && (
                  <div>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <label
                          htmlFor="context"
                          className="block text-sm font-semibold text-gray-800 leading-tight mb-2"
                        >
                          Context <span className="text-red-500" aria-label="required">*</span>
                        </label>
                        <p className="mt-0.5 mb-2 text-xs text-gray-500 leading-snug">
                          Tell the AI what to focus on so question generation stays relevant.
                        </p>
                      </div>
                      {shouldShowJdModalCta && (
                        <button
                          type="button"
                          onClick={() => setShowJdUploadModal(true)}
                          disabled={jdProcessing}
                          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#007BFF]/25 bg-[#007BFF]/5 px-3 py-1.5 text-xs font-semibold text-[#007BFF] hover:bg-[#007BFF]/10 disabled:cursor-not-allowed disabled:opacity-60 transition-all"
                        >
                          {jdProcessing ? (
                            <span className="inline-flex items-center gap-1.5">
                              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#007BFF] border-t-transparent" />
                              Processing...
                            </span>
                          ) : (
                            <>
                              <DocumentDuplicateIcon className="h-4 w-4" />
                              Upload JD
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    <textarea
                      id="context"
                      name="context"
                      data-testid="interview-context-textarea"
                      value={formData.context}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] transition-all resize-none text-sm"
                      placeholder="Describe the role focus, key skills, must-evaluate topics, or any interview priorities."
                      maxLength={5000}
                    />
                    {shouldShowJdModalCta && jdExtractedSummary ? (
                      <div className="mt-2 flex items-center justify-end gap-2 text-xs">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700">
                          <CheckIcon className="h-3.5 w-3.5" />
                          JD uploaded
                        </span>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Edit Questions or Modules */}
            {currentStep === 2 && (
              <div className="space-y-4 max-w-4xl">
                {isEditLibrary && editLibraryLoading ? (
                  <div className="flex items-center justify-center py-24">
                    <div className="h-10 w-10 border-2 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : advancedType === ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED ? (
                  <>
                    {/* Introduction Message */}
                    <div className="rounded-lg border border-gray-200 bg-white">
                      <div className="flex items-center justify-between p-4">
                        <button
                          type="button"
                          onClick={() => setExpandedIntro((prev) => !prev)}
                          className="flex items-center gap-3 text-left min-w-0"
                        >
                          <SparklesIcon className="w-5 h-5 text-[#007BFF]" />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900">Introduction Message</p>
                            <p className="text-xs text-gray-500 line-clamp-1">
                              {formData.aiIntro
                                ? formData.aiIntro.length > 80
                                  ? `${formData.aiIntro.substring(0, 77).trim()}...`
                                  : formData.aiIntro
                                : 'No introduction set'}
                            </p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setExpandedIntro((prev) => !prev)}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          aria-expanded={expandedIntro}
                        >
                          {expandedIntro ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <div
                        className={`overflow-hidden transition-[max-height] duration-200 ease-out ${expandedIntro ? 'max-h-[400px]' : 'max-h-0'
                          }`}
                      >
                        <div className="p-4 border-t border-gray-100 space-y-2">
                          <textarea
                            value={formData.aiIntro}
                            onChange={(e) =>
                              setFormData((prev) => ({ ...prev, aiIntro: e.target.value }))
                            }
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] resize-none text-sm bg-white"
                            placeholder="Hi {candidateName}, welcome to this interview..."
                          />
                        </div>
                      </div>
                    </div>

                    <RubricModulesEditor
                      modules={rubricModules}
                      setModules={setRubricModules}
                      interviewTitle={formData.title}
                      showUploadButton={true}
                    />

                    {/* Closing Message */}
                    <div className="rounded-lg border border-gray-200 bg-white">
                      <div className="flex items-center justify-between p-4">
                        <button
                          type="button"
                          onClick={() => setExpandedOutro((prev) => !prev)}
                          className="flex items-center gap-3 text-left min-w-0"
                        >
                          <CheckIcon className="w-5 h-5 text-[#007BFF]" />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900">Closing Message</p>
                            <p className="text-xs text-gray-500 line-clamp-1">
                              {formData.aiOutro
                                ? formData.aiOutro.length > 80
                                  ? `${formData.aiOutro.substring(0, 77).trim()}...`
                                  : formData.aiOutro
                                : 'No closing set'}
                            </p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setExpandedOutro((prev) => !prev)}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          aria-expanded={expandedOutro}
                        >
                          {expandedOutro ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <div
                        className={`overflow-hidden transition-[max-height] duration-200 ease-out ${expandedOutro ? 'max-h-[400px]' : 'max-h-0'
                          }`}
                      >
                        <div className="p-4 border-t border-gray-100 space-y-2">
                          <textarea
                            value={formData.aiOutro}
                            onChange={(e) =>
                              setFormData((prev) => ({ ...prev, aiOutro: e.target.value }))
                            }
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] resize-none text-sm bg-white"
                            placeholder="Thank you {candidateName} for completing this interview..."
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                {/* Loading State for Auto-Generation */}
                {isAutoGenerating && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-[#007BFF]">
                      <SparklesIcon className="w-4 h-4 animate-pulse" />
                      <span>AI is generating questions...</span>
                    </div>
                    {generatingQuestions.map((question, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-4 bg-white border border-[#007BFF]/20 rounded-lg shadow-sm animate-fadeIn"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <div
                          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${question.section === 'eligibility'
                              ? 'bg-yellow-500 text-white'
                              : question.section === 'behavioral'
                                ? 'bg-green-500 text-white'
                                : question.section === 'cultural'
                                  ? 'bg-[#007BFF] text-white'
                                  : 'bg-blue-500 text-white'
                            }`}
                        >
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 font-medium">{question.prompt}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium ${question.section === 'eligibility'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : question.section === 'behavioral'
                                    ? 'bg-green-100 text-green-700'
                                    : question.section === 'cultural'
                                      ? 'bg-[#007BFF]/10 text-[#0062CC]'
                                      : 'bg-blue-100 text-blue-700'
                                }`}
                            >
                              {question.section === 'eligibility'
                                ? 'Eligibility'
                                : question.section === 'behavioral'
                                  ? 'Behavioral'
                                  : question.section === 'cultural'
                                    ? 'Cultural'
                                    : 'Technical'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Edit Content */}
                {!isAutoGenerating && (
                  <>

                    {/* Interview Language and Voice (edit flow only — Create flow picks this in step 1) */}
                    {isEditLibrary && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start mb-6">
                        {/* Language selector */}
                        <div className="rounded-lg border border-gray-200 bg-white p-4">
                          <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                            Interview language
                          </label>
                          <p className="text-xs text-gray-500 mb-2 min-h-[2rem]">
                            Choose the language the interviewer will speak.
                          </p>
                          <div className="relative" ref={langDropdownRef}>
                            {(() => {
                              const langOptions = LANGUAGE_OPTIONS;
                              const selectedLang = langOptions.find((l) => l.id === interviewLanguageCode);

                              return (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setLangDropdownOpen((o) => !o);
                                      setInterviewModeDropdownOpen(false);
                                      setVoiceDropdownOpen(false);
                                    }}
                                    className={`flex w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-left text-sm shadow-sm transition-all duration-200 ${langDropdownOpen
                                        ? 'border-[#007BFF] ring-2 ring-[#007BFF]/25'
                                        : 'border-gray-300 hover:border-[#007BFF]/60 hover:bg-gray-50'
                                      }`}
                                  >
                                    <div className="flex min-w-0 items-center gap-2">
                                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-[#007BFF] to-[#0056b3] text-white shadow-sm">
                                        <span className="text-[10px] font-bold leading-none">{selectedLang?.icon || 'हि'}</span>
                                      </div>
                                      <div className="min-w-0">
                                        <p
                                          className={`truncate font-semibold ${selectedLang ? 'text-gray-900' : 'text-gray-400'
                                            }`}
                                        >
                                          {selectedLang ? selectedLang.name : 'Select language…'}
                                        </p>
                                        <p className="truncate text-[11px] text-gray-500">
                                          {selectedLang ? selectedLang.desc : 'Select a language'}
                                        </p>
                                      </div>
                                    </div>
                                    <ChevronDownIcon
                                      className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${langDropdownOpen ? 'rotate-180' : ''
                                        }`}
                                    />
                                  </button>

                                  {langDropdownOpen && (
                                    <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-xl max-h-64 overflow-y-auto">
                                      {langOptions.map(({ id, name, icon }) => {
                                        const isSelected = interviewLanguageCode === id;
                                        return (
                                          <div
                                            key={id}
                                            className={`flex cursor-pointer items-center gap-2.5 px-3 py-2.5 transition-all ${isSelected
                                                ? 'border-l-3 border-l-[#007BFF] bg-[#007BFF]/5'
                                                : 'border-l-3 border-l-transparent hover:bg-gray-50'
                                              }`}
                                            onClick={() => {
                                              setInterviewLanguageCode(id);
                                              setLangDropdownOpen(false);
                                            }}
                                          >
                                            <div
                                              className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${isSelected
                                                  ? 'bg-gradient-to-br from-[#007BFF] to-[#0056b3] text-white shadow-sm'
                                                  : 'bg-gray-100 text-gray-500'
                                                }`}
                                            >
                                              <span className="text-xs font-bold leading-none">{icon}</span>
                                            </div>
                                            <span
                                              className={`text-sm font-semibold ${isSelected ? 'text-[#007BFF]' : 'text-gray-900'
                                                }`}
                                            >
                                              {name}
                                            </span>
                                            {isSelected && (
                                              <CheckIcon className="ml-auto h-5 w-5 flex-shrink-0 text-[#007BFF]" />
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Interviewer Voice */}
                        <div className="rounded-lg border border-gray-200 bg-white p-4">
                          <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                            Interviewer Voice
                          </label>
                        <p className="text-xs text-gray-500 mb-1.5">
                          {['hi', 'hi-en', 'en-hi', 'kn', 'ta', 'bn', 'te', 'mr', 'gu', 'ml', 'pa', 'tl', 'fr'].includes(interviewLanguageCode)
                            ? 'Choose a native voice for the AI interviewer.'
                            : 'Choose the voice accent for the AI interviewer.'}
                        </p>
                        <div className="relative" ref={voiceDropdownRef}>
                          {(() => {
                            const voiceOptions = VOICE_OPTIONS_BY_LANGUAGE[interviewLanguageCode] || englishVoiceOptions;
                            const selectedVoice = voiceOptions.find(v => v.id === formData.voiceId) || voiceOptions[0];

                            return (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setVoiceDropdownOpen((o) => !o);
                                    setInterviewModeDropdownOpen(false);
                                    setLangDropdownOpen(false);
                                  }}
                                  className={`flex w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-left text-sm shadow-sm transition-all duration-200 ${voiceDropdownOpen
                                      ? 'border-[#007BFF] ring-2 ring-[#007BFF]/25'
                                      : 'border-gray-300 hover:border-[#007BFF]/60 hover:bg-gray-50'
                                    }`}
                                >
                                  <div className="flex min-w-0 items-center gap-2">
                                    <div
                                      className="flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-md bg-[#007BFF]/10 text-[#007BFF] transition-colors hover:bg-[#007BFF]/20"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleVoicePreview(selectedVoice.id, selectedVoice.preview);
                                      }}
                                      title="Preview voice"
                                    >
                                      {playingVoiceId === selectedVoice.id ? (
                                        <PauseIcon className="h-4 w-4" />
                                      ) : (
                                        <PlayIcon className="h-4 w-4" />
                                      )}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="truncate font-semibold text-gray-900">
                                        {selectedVoice.gender}
                                        <span className="font-medium text-gray-400"> · </span>
                                        <span className="font-medium text-gray-700">{selectedVoice.accent}</span>
                                      </p>
                                      <p className="truncate text-left text-[11px] text-gray-500">
                                        {selectedVoice.tone}
                                      </p>
                                    </div>
                                  </div>
                                  <ChevronDownIcon
                                    className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${voiceDropdownOpen ? 'rotate-180' : ''}`}
                                  />
                                </button>

                                {voiceDropdownOpen && (
                                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                                    {voiceOptions.map(({ id, gender, accent, tone, preview }) => {
                                      const isSelected = formData.voiceId === id;
                                      return (
                                        <div
                                          key={id}
                                          className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-all ${isSelected
                                              ? 'bg-[#007BFF]/5 border-l-3 border-l-[#007BFF]'
                                              : 'hover:bg-gray-50 border-l-3 border-l-transparent'
                                            }`}
                                          onClick={() => {
                                            setFormData((prev) => ({ ...prev, voiceId: id }));
                                            setVoiceDropdownOpen(false);
                                          }}
                                        >
                                          <div
                                            className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all ${playingVoiceId === id
                                                ? 'bg-gradient-to-br from-[#007BFF] to-[#0056b3] text-white shadow-sm'
                                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                              }`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleVoicePreview(id, preview);
                                            }}
                                            title="Preview voice"
                                          >
                                            {playingVoiceId === id ? (
                                              <PauseIcon className="w-4 h-4" />
                                            ) : (
                                              <PlayIcon className="w-4 h-4" />
                                            )}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                              <span className={`text-sm font-semibold ${isSelected ? 'text-[#007BFF]' : 'text-gray-900'}`}>{gender}</span>
                                              <span className="text-gray-300">•</span>
                                              <span className={`text-sm font-medium ${isSelected ? 'text-[#007BFF]' : 'text-gray-700'}`}>{accent}</span>
                                            </div>
                                            <p className="text-xs text-gray-500">{tone}</p>
                                          </div>
                                          {isSelected && (
                                            <CheckIcon className="w-5 h-5 text-[#007BFF] flex-shrink-0" />
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                        <p className="text-xs text-gray-500 mt-1.5">
                          Click the play button to preview.
                        </p>
                      </div>
                      </div>
                    )}

                    {/* Introduction Message */}
                    <div className="rounded-lg border border-gray-200 bg-white">
                      <div className="flex items-center justify-between p-4">
                        <button
                          type="button"
                          onClick={() => setExpandedIntro((prev) => !prev)}
                          className="flex items-center gap-3 text-left min-w-0"
                        >
                          <SparklesIcon className="w-5 h-5 text-[#007BFF]" />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900">Introduction Message</p>
                            <p className="text-xs text-gray-500 line-clamp-1">
                              {formData.aiIntro
                                ? formData.aiIntro.length > 80
                                  ? `${formData.aiIntro.substring(0, 77).trim()}...`
                                  : formData.aiIntro
                                : 'No introduction set'}
                            </p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setExpandedIntro((prev) => !prev)}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          aria-expanded={expandedIntro}
                        >
                          {expandedIntro ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <div
                        className={`overflow-hidden transition-[max-height] duration-200 ease-out ${expandedIntro ? 'max-h-[400px]' : 'max-h-0'
                          }`}
                      >
                        <div className="p-4 border-t border-gray-100 space-y-2">
                          <textarea
                            value={formData.aiIntro}
                            onChange={(e) =>
                              setFormData((prev) => ({ ...prev, aiIntro: e.target.value }))
                            }
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] resize-none text-sm bg-white"
                            placeholder="Hi {candidateName}, welcome to this interview..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Questions List — Accordion style */}
                    {formData.questions.length > 0 ? (
                      <DragDropContext onDragEnd={handleQuestionDragEnd}>
                        <Droppable droppableId="questions-step2">
                          {(provided) => (
                            <div
                              id="ai-interview-questions-list"
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              className="space-y-3"
                            >
                              {formData.questions.map((question, index) => (
                                <Draggable
                                  key={`step2-q-${index}`}
                                  draggableId={`step2-q-${index}`}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className={`rounded-lg border transition-all ${snapshot.isDragging
                                          ? 'shadow-lg border-purple-300 bg-purple-50'
                                          : 'border-gray-200 bg-white hover:border-gray-300'
                                        }`}
                                    >
                                      <div className="flex items-start gap-2 p-4">
                                        <div
                                          {...provided.dragHandleProps}
                                          className="flex-shrink-0 mt-0.5 cursor-grab active:cursor-grabbing"
                                        >
                                          <Bars3Icon className="w-5 h-5 text-gray-400" />
                                        </div>
                                        <div
                                          className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${question.section === 'eligibility'
                                              ? 'bg-yellow-500 text-white'
                                              : question.section === 'behavioral'
                                                ? 'bg-green-500 text-white'
                                                : question.section === 'cultural'
                                                  ? 'bg-purple-500 text-white'
                                                  : 'bg-blue-500 text-white'
                                            }`}
                                        >
                                          {index + 1}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          {/* Accordion header — always visible */}
                                          <div className="flex items-start justify-between gap-2">
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setExpandedQuestionIndex((prev) => {
                                                  const next = prev === index ? null : index;
                                                  if (next === index) {
                                                    const current = formData.questions[index] || {};
                                                    questionEditSnapshotsRef.current = {
                                                      ...questionEditSnapshotsRef.current,
                                                      [index]: {
                                                        expectedPoints: Array.isArray(
                                                          current.expectedPoints
                                                        )
                                                          ? [...current.expectedPoints]
                                                          : [],
                                                        expectedAnswer: current.expectedAnswer || '',
                                                      },
                                                    };
                                                  }
                                                  return next;
                                                });
                                              }}
                                              className="flex-1 min-w-0 text-left"
                                            >
                                              <p className="text-sm font-medium text-gray-900 line-clamp-2">
                                                {question.prompt || 'No question text'}
                                              </p>
                                              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                                <span
                                                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${question.section === 'eligibility'
                                                      ? 'bg-yellow-100 text-yellow-700'
                                                      : question.section === 'behavioral'
                                                        ? 'bg-green-100 text-green-700'
                                                        : question.section === 'cultural'
                                                          ? 'bg-purple-100 text-purple-700'
                                                          : 'bg-blue-100 text-blue-700'
                                                    }`}
                                                >
                                                  {question.section === 'eligibility'
                                                    ? 'Eligibility'
                                                    : question.section === 'behavioral'
                                                      ? 'Behavioral'
                                                      : question.section === 'cultural'
                                                        ? 'Cultural'
                                                        : 'Technical'}
                                                </span>
                                                {question.type === 'yes_no' && question.critical && (
                                                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-red-100 text-red-700 border border-red-200">
                                                    Critical
                                                  </span>
                                                )}
                                                <span className="text-xs text-gray-500">
                                                  {question.type === 'yes_no'
                                                    ? 'Yes/No'
                                                    : 'Detailed'}
                                                </span>
                                              </div>
                                            </button>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setExpandedQuestionIndex((prev) => {
                                                    const next =
                                                      prev === index ? null : index;
                                                    if (next === index) {
                                                      const current =
                                                        formData.questions[index] || {};
                                                      questionEditSnapshotsRef.current = {
                                                        ...questionEditSnapshotsRef.current,
                                                        [index]: {
                                                          expectedPoints: Array.isArray(
                                                            current.expectedPoints
                                                          )
                                                            ? [...current.expectedPoints]
                                                            : [],
                                                          expectedAnswer: current.expectedAnswer || '',
                                                        },
                                                      };
                                                    }
                                                    return next;
                                                  });
                                                }}
                                                className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                                                aria-expanded={expandedQuestionIndex === index}
                                              >
                                                {expandedQuestionIndex === index ? (
                                                  <ChevronUpIcon className="w-4 h-4" />
                                                ) : (
                                                  <ChevronDownIcon className="w-4 h-4" />
                                                )}
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => handleRemoveQuestion(index)}
                                                data-testid="remove-question-btn"
                                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                                              >
                                                <TrashIcon className="w-4 h-4" />
                                              </button>
                                            </div>
                                          </div>
                                          {/* Accordion body — with slide transition */}
                                          <div
                                            className={`overflow-hidden transition-[max-height] duration-200 ease-out ${expandedQuestionIndex === index
                                                ? 'max-h-[600px]'
                                                : 'max-h-0'
                                              }`}
                                          >
                                            <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                                              <textarea
                                                value={question.prompt}
                                                onChange={(e) =>
                                                  updateQuestion(index, { prompt: e.target.value })
                                                }
                                                data-testid="question-prompt-input"
                                                rows={2}
                                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] resize-none"
                                                placeholder="Enter your question..."
                                              />
                                              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
                                                <div className="flex items-center gap-2">
                                                  <span className="text-xs font-medium text-gray-600">
                                                    Category:
                                                  </span>
                                                  <select
                                                    value={normalizeSectionForCategorySelect(
                                                      question.section
                                                    )}
                                                    onChange={(e) =>
                                                      updateQuestion(index, {
                                                        section: e.target.value,
                                                      })
                                                    }
                                                    className="text-xs px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 min-w-[148px]"
                                                    aria-label="Question category"
                                                  >
                                                    <option value="eligibility">Eligibility</option>
                                                    <option value="technical">Technical</option>
                                                    <option value="behavioral">Behavioral</option>
                                                    <option value="cultural">Cultural</option>
                                                  </select>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  <span className="text-xs font-medium text-gray-600">
                                                    Type:
                                                  </span>
                                                  <select
                                                    value={question.type}
                                                    onChange={(e) =>
                                                      updateQuestion(index, { type: e.target.value })
                                                    }
                                                    className="text-xs px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25"
                                                  >
                                                    <option value="short">Detailed Answer</option>
                                                    <option value="yes_no">Yes/No</option>
                                                  </select>
                                                  {question.type === 'yes_no' && (
                                                    <label className="flex items-center gap-1.5 ml-2">
                                                      <input
                                                        type="checkbox"
                                                        checked={Boolean(question.critical)}
                                                        onChange={(e) =>
                                                          updateQuestion(index, {
                                                            critical: e.target.checked,
                                                          })
                                                        }
                                                        className="h-3.5 w-3.5 accent-red-600"
                                                      />
                                                      <span className="text-xs font-medium text-red-700">
                                                        Critical
                                                      </span>
                                                    </label>
                                                  )}
                                                </div>
                                              </div>

                                              {question.type === 'yes_no' && (
                                                <div className="p-3 bg-indigo-50/60 rounded-lg border border-indigo-100">
                                                  <fieldset>
                                                    <legend className="text-xs font-medium text-indigo-700 mb-2">
                                                      Expected answer
                                                    </legend>
                                                    <div className="flex items-center justify-between mb-2">
                                                      <p className="text-xs text-gray-600">
                                                        Which response counts as correct for scoring?
                                                      </p>
                                                      <button
                                                        type="button"
                                                        onClick={() => handleAutoGenerateExpected(index)}
                                                        disabled={autoGeneratingIndices.has(index) || !question.prompt?.trim()}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                      >
                                                        {autoGeneratingIndices.has(index) ? (
                                                          <>
                                                            <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Thinking...
                                                          </>
                                                        ) : (
                                                          <>
                                                            <SparklesIcon className="w-3 h-3" />
                                                            Generate
                                                          </>
                                                        )}
                                                      </button>
                                                    </div>
                                                    <div className="flex flex-wrap gap-5">
                                                      <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-800">
                                                        <input
                                                          type="radio"
                                                          name={`yes-no-expected-${index}`}
                                                          checked={
                                                            normalizeYesNoExpected(
                                                              question.expectedAnswer
                                                            ) === 'Yes'
                                                          }
                                                          onChange={() =>
                                                            updateQuestion(index, {
                                                              expectedAnswer: 'Yes',
                                                            })
                                                          }
                                                          className="h-4 w-4 accent-[#007BFF]"
                                                        />
                                                        Yes
                                                      </label>
                                                      <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-800">
                                                        <input
                                                          type="radio"
                                                          name={`yes-no-expected-${index}`}
                                                          checked={
                                                            normalizeYesNoExpected(
                                                              question.expectedAnswer
                                                            ) === 'No'
                                                          }
                                                          onChange={() =>
                                                            updateQuestion(index, {
                                                              expectedAnswer: 'No',
                                                            })
                                                          }
                                                          className="h-4 w-4 accent-[#007BFF]"
                                                        />
                                                        No
                                                      </label>
                                                    </div>
                                                  </fieldset>
                                                </div>
                                              )}
                                              {question.type !== 'yes_no' && (
                                                <div className="p-3 bg-indigo-50/60 rounded-lg border border-indigo-100 space-y-2">
                                                  <div className="flex items-center justify-between gap-2">
                                                    <label className="block text-xs font-medium text-indigo-700">
                                                      Expected Answer Points (for scoring)
                                                    </label>
                                                    <div className="flex items-center gap-2">
                                                      <button
                                                        type="button"
                                                        onClick={() => handleAutoGenerateExpected(index)}
                                                        disabled={autoGeneratingIndices.has(index) || !question.prompt?.trim()}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                      >
                                                        {autoGeneratingIndices.has(index) ? (
                                                          <>
                                                            <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Thinking...
                                                          </>
                                                        ) : (
                                                          <>
                                                            <SparklesIcon className="w-3 h-3" />
                                                            Generate
                                                          </>
                                                        )}
                                                      </button>
                                                      <button
                                                        type="button"
                                                        onClick={() => {
                                                          const prevPoints = Array.isArray(question.expectedPoints)
                                                            ? question.expectedPoints
                                                            : [];
                                                          updateQuestion(index, {
                                                            expectedPoints: [...prevPoints, ''],
                                                          });
                                                        }}
                                                        className="text-xs px-2 py-1 rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                                                      >
                                                        + Add point
                                                      </button>
                                                    </div>
                                                  </div>

                                                  {Array.isArray(question.expectedPoints) &&
                                                    question.expectedPoints.length > 0 ? (
                                                    <div className="space-y-2">
                                                      {question.expectedPoints.map((point, pIdx) => (
                                                        <div key={pIdx} className="flex items-start gap-2">
                                                          <span className="mt-2 flex-shrink-0 w-5 h-5 rounded-full bg-indigo-200 text-indigo-700 flex items-center justify-center text-[10px] font-bold">
                                                            {pIdx + 1}
                                                          </span>
                                                          <input
                                                            value={typeof point === 'string' ? point : ''}
                                                            onChange={(e) => {
                                                              const prevPoints = Array.isArray(question.expectedPoints)
                                                                ? question.expectedPoints
                                                                : [];
                                                              const nextPoints = [...prevPoints];
                                                              nextPoints[pIdx] = e.target.value;
                                                              updateQuestion(index, { expectedPoints: nextPoints });
                                                            }}
                                                            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF]"
                                                            placeholder="Enter a scoring point..."
                                                          />
                                                          <button
                                                            type="button"
                                                            onClick={() => {
                                                              const prevPoints = Array.isArray(question.expectedPoints)
                                                                ? question.expectedPoints
                                                                : [];
                                                              updateQuestion(index, {
                                                                expectedPoints: prevPoints.filter((_, i) => i !== pIdx),
                                                              });
                                                            }}
                                                            className="mt-1 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                                            aria-label="Remove point"
                                                          >
                                                            <TrashIcon className="w-4 h-4" />
                                                          </button>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  ) : (
                                                    <p className="text-xs text-gray-600">
                                                      Add at least one point to enable scoring for this question.
                                                    </p>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                            <div className="mt-2 flex justify-end gap-2">
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const isPendingNewQuestion =
                                                    Boolean(
                                                      pendingQuestionLocalId &&
                                                      question?.localId === pendingQuestionLocalId
                                                    );

                                                  // If the user cancels the just-created placeholder, remove it.
                                                  if (isPendingNewQuestion) {
                                                    setFormData((prev) => ({
                                                      ...prev,
                                                      questions: prev.questions.filter(
                                                        (_, i) => i !== index
                                                      ),
                                                    }));
                                                    setPendingQuestionLocalId(null);
                                                    pendingQuestionLockRef.current = false;
                                                    setExpandedQuestionIndex(null);
                                                    return;
                                                  }

                                                  const snapshot =
                                                    questionEditSnapshotsRef.current[index];
                                                  if (snapshot) {
                                                    if (question.type === 'yes_no') {
                                                      updateQuestion(index, {
                                                        expectedAnswer: snapshot.expectedAnswer || '',
                                                      });
                                                    } else {
                                                      updateQuestion(index, {
                                                        expectedPoints:
                                                          Array.isArray(snapshot.expectedPoints)
                                                            ? [...snapshot.expectedPoints]
                                                            : [],
                                                      });
                                                    }
                                                  }
                                                  setExpandedQuestionIndex(null);
                                                }}
                                                className="px-3 py-1.5 text-xs rounded-md border border-red-200 text-red-600 bg-red-50 hover:bg-red-100"
                                              >
                                                Cancel
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const isPendingNewQuestion =
                                                    Boolean(
                                                      pendingQuestionLocalId &&
                                                      question?.localId === pendingQuestionLocalId
                                                    );

                                                  if (isPendingNewQuestion) {
                                                    const trimmedPrompt = (question?.prompt || '').trim();
                                                    if (!trimmedPrompt) {
                                                      toast.error(
                                                        'Please enter the question text before saving.'
                                                      );
                                                      return;
                                                    }

                                                    setPendingQuestionLocalId(null);
                                                    pendingQuestionLockRef.current = false;
                                                  }

                                                  setExpandedQuestionIndex(null);
                                                }}
                                                className="px-3 py-1.5 text-xs rounded-md bg-[#007BFF] text-white hover:bg-[#0064cc]"
                                              >
                                                Save
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-center rounded-lg border-2 border-dashed border-gray-200 bg-gray-50">
                        <SparklesIcon className="w-12 h-12 text-gray-400 mb-3" />
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">
                          No Questions Yet
                        </h3>
                        <p className="text-xs text-gray-500">
                          Add a question to get started
                        </p>
                        <button
                          type="button"
                          onClick={handleAddQuestion}
                          disabled={
                            pendingQuestionLocalId !== null ||
                            formData.questions.length >= MAX_INTERVIEW_QUESTIONS_TOTAL
                          }
                          aria-disabled={
                            pendingQuestionLocalId !== null ||
                            formData.questions.length >= MAX_INTERVIEW_QUESTIONS_TOTAL
                          }
                          className="mt-4 px-3 py-1.5 border border-gray-200 text-gray-700 rounded-md hover:bg-gray-100 flex items-center gap-1 text-sm font-medium transition-all disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                          data-testid="add-first-question-btn"
                        >
                          <PlusIcon className="w-4 h-4" />
                          <span>Add Question</span>
                        </button>
                      </div>
                    )}

                    {/* Closing Message */}
                    <div className="rounded-lg border border-gray-200 bg-white">
                      <div className="flex items-center justify-between p-4">
                        <button
                          type="button"
                          onClick={() => setExpandedOutro((prev) => !prev)}
                          className="flex items-center gap-3 text-left min-w-0"
                        >
                          <CheckIcon className="w-5 h-5 text-[#007BFF]" />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900">Closing Message</p>
                            <p className="text-xs text-gray-500 line-clamp-1">
                              {formData.aiOutro
                                ? formData.aiOutro.length > 80
                                  ? `${formData.aiOutro.substring(0, 77).trim()}...`
                                  : formData.aiOutro
                                : 'No closing set'}
                            </p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setExpandedOutro((prev) => !prev)}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          aria-expanded={expandedOutro}
                        >
                          {expandedOutro ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <div
                        className={`overflow-hidden transition-[max-height] duration-200 ease-out ${expandedOutro ? 'max-h-[400px]' : 'max-h-0'
                          }`}
                      >
                        <div className="p-4 border-t border-gray-100 space-y-2">
                          <textarea
                            value={formData.aiOutro}
                            onChange={(e) =>
                              setFormData((prev) => ({ ...prev, aiOutro: e.target.value }))
                            }
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] resize-none text-sm bg-white"
                            placeholder="Thank you {candidateName} for completing this interview..."
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
                  </>
                )}
              </div>
            )}

            {/* Step 4: Invite & Share */}
            {currentStep === 4 && (
              <div className="space-y-5">
                {/* Share Options - Smaller Grid */}
                <div className="grid grid-cols-4 gap-3">
                  {/* Email */}
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, activeSharePlatform: 'email' }))
                    }
                    className={`p-3 rounded-lg border-2 transition-all text-center ${formData.activeSharePlatform === 'email'
                        ? 'border-[#007BFF] bg-[#007BFF]/5'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                  >
                    <div className="w-10 h-10 bg-[#007BFF] rounded-lg flex items-center justify-center mx-auto mb-2">
                      <EnvelopeIcon className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-xs font-medium text-gray-900">Email</p>
                  </button>

                  {/* WhatsApp */}
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, activeSharePlatform: 'whatsapp' }))
                    }
                    className={`p-3 rounded-lg border-2 transition-all text-center ${formData.activeSharePlatform === 'whatsapp'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                  >
                    <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                    </div>
                    <p className="text-xs font-medium text-gray-900">WhatsApp</p>
                  </button>

                  {/* LinkedIn */}
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, activeSharePlatform: 'linkedin' }))
                    }
                    className={`p-3 rounded-lg border-2 transition-all text-center ${formData.activeSharePlatform === 'linkedin'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                  >
                    <div className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </div>
                    <p className="text-xs font-medium text-gray-900">LinkedIn</p>
                  </button>

                  {/* Copy Link */}
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, activeSharePlatform: 'link' }))
                    }
                    className={`p-3 rounded-lg border-2 transition-all text-center ${formData.activeSharePlatform === 'link'
                        ? 'border-gray-500 bg-gray-100'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                  >
                    <div className="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <LinkIcon className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-xs font-medium text-gray-900">Copy Link</p>
                  </button>
                </div>

                {/* Platform-specific Preview */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Email Preview */}
                  {(formData.activeSharePlatform === 'email' || !formData.activeSharePlatform) && (
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <EnvelopeIcon className="w-4 h-4 text-[#007BFF]" />
                          <span className="text-sm font-medium text-gray-700">Email Preview</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(emailPreview);
                            toast.success('Email copied to clipboard!');
                          }}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-[#007BFF] hover:bg-[#007BFF]/5 rounded transition-colors"
                        >
                          <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                          Copy
                        </button>
                      </div>
                      <textarea
                        value={emailPreview}
                        onChange={(e) => {
                          emailPreviewEditedRef.current = true;
                          setEmailPreview(e.target.value);
                        }}
                        rows={16}
                        className="w-full px-3 py-2 border border-gray-200 rounded-md text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#007BFF] resize-none bg-gray-50"
                        placeholder="Email preview..."
                      />
                      {/* Invite Settings */}
                      <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                              Invite Link Expiry
                            </label>
                            <select
                              value={formData.inviteExpiry}
                              onChange={(e) =>
                                setFormData((prev) => ({ ...prev, inviteExpiry: e.target.value }))
                              }
                              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#007BFF]"
                            >
                              <option value="1-week">1 Week (Default)</option>
                              <option value="2-weeks">2 Weeks</option>
                              <option value="1-month">1 Month</option>
                              <option value="2-months">2 Months</option>
                              <option value="3-months">3 Months</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                              Email Reminder Frequency
                            </label>
                            <select
                              value={formData.emailReminderFrequency}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  emailReminderFrequency: e.target.value,
                                }))
                              }
                              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#007BFF]"
                            >
                              <option value="none">No Reminders</option>
                              <option value="daily">Daily</option>
                              <option value="every-2-days">Every 2 Days</option>
                              <option value="every-3-days">Every 3 Days</option>
                              <option value="weekly">Weekly</option>
                            </select>
                          </div>
                        </div>
                        {formData.emailReminderFrequency !== 'none' && (
                          <p className="text-xs text-gray-500 bg-[#007BFF]/5 p-2 rounded">
                            📧 Reminder emails will be sent{' '}
                            {formData.emailReminderFrequency === 'daily'
                              ? 'every day'
                              : formData.emailReminderFrequency === 'every-2-days'
                                ? 'every 2 days'
                                : formData.emailReminderFrequency === 'every-3-days'
                                  ? 'every 3 days'
                                  : 'every week'}{' '}
                            until the candidate completes the interview or the link expires.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* WhatsApp Preview */}
                  {formData.activeSharePlatform === 'whatsapp' && (
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <svg
                          className="w-4 h-4 text-green-600"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        <span className="text-sm font-medium text-gray-700">WhatsApp Draft</span>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                        <p className="text-sm text-gray-700 whitespace-pre-line">{`Hi [Candidate Name]! 👋

You've been invited to take an AI interview for: ${formData.title || '[Interview Title]'}

🔗 Start here: [Interview Link]

⏰ Please complete within ${formData.inviteExpiry === '1-week' ? '1 week' : formData.inviteExpiry === '2-weeks' ? '2 weeks' : formData.inviteExpiry === '1-month' ? '1 month' : formData.inviteExpiry === '2-months' ? '2 months' : '3 months'}

Good luck! 🍀`}</p>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">Copy the draft above to share via WhatsApp</p>
                    </div>
                  )}

                  {/* LinkedIn Preview */}
                  {formData.activeSharePlatform === 'linkedin' && (
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <svg
                          className="w-4 h-4 text-blue-700"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                        <span className="text-sm font-medium text-gray-700">
                          LinkedIn InMail Draft
                        </span>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                        <p className="text-sm font-medium text-gray-900 mb-2">
                          Subject: Interview Invitation - ${formData.title || '[Interview Title]'}
                        </p>
                        <p className="text-sm text-gray-700 whitespace-pre-line">{`Hi [Candidate Name],

I hope this message finds you well. We're impressed with your profile and would like to invite you to take an AI-powered interview for the position mentioned above.

Interview Link: [Link]

This interview will assess your qualifications and should take approximately 15-20 minutes. Please complete it at your convenience within the next ${formData.inviteExpiry === '1-week' ? 'week' : formData.inviteExpiry === '2-weeks' ? 'two weeks' : formData.inviteExpiry === '1-month' ? 'month' : formData.inviteExpiry === '2-months' ? 'two months' : 'three months'}.

Best regards,
[Your Name]`}</p>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">LinkedIn integration coming soon</p>
                    </div>
                  )}

                  {/* Copy Link Preview */}
                  {formData.activeSharePlatform === 'link' && (
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <LinkIcon className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-700">Interview Link</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          value="[Link will be generated after creation]"
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-500"
                        />
                        <button
                          type="button"
                          className="px-3 py-2 bg-gray-600 text-white rounded-md text-sm hover:bg-gray-700 flex items-center gap-1"
                          onClick={() =>
                            toast.info('Link will be available after creating the interview')
                          }
                        >
                          <DocumentDuplicateIcon className="w-4 h-4" />
                          Copy
                        </button>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        Share this link anywhere - social media, messaging apps, etc.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Proctoring Settings */}
            {currentStep === 3 && (
              <div className="space-y-6 max-w-4xl pb-4" style={{ minHeight: 'fit-content' }}>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Interview Proctoring Options</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Enable these features to monitor candidate behavior and ensure interview integrity during voice (audio) and video interviews.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {/* Browser Activity Monitoring */}
                  <label className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:border-[#007BFF]/30 hover:bg-[#007BFF]/[0.02] cursor-pointer group">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-gray-900 block">Browser Activity Monitoring</span>
                      <span className="text-xs text-gray-500 mt-1 block">
                        Tracks browser tabs, windows, and application switches to detect when candidates leave interview environment.
                      </span>
                    </div>
                    <span
                      className={`inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full p-0.5 cursor-pointer border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-[#007BFF] focus-within:ring-offset-2 ${formData.proctoring.browserActivityMonitoring
                          ? 'bg-[#007BFF] justify-end'
                          : 'bg-gray-200 justify-start'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.proctoring.browserActivityMonitoring}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            proctoring: {
                              ...prev.proctoring,
                              browserActivityMonitoring: e.target.checked,
                            },
                          }))
                        }
                        className="sr-only"
                      />
                      <span className="h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out" />
                    </span>
                  </label>

                  {/* Response Delay Tracking */}
                  <label className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:border-[#007BFF]/30 hover:bg-[#007BFF]/[0.02] cursor-pointer group">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-gray-900 block">Response Delay Tracking</span>
                      <span className="text-xs text-gray-500 mt-1 block">
                        Monitors time between questions and answers to identify unusually long pauses that may indicate external assistance or reading prepared responses.
                      </span>
                    </div>
                    <span
                      className={`inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full p-0.5 cursor-pointer border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-[#007BFF] focus-within:ring-offset-2 ${formData.proctoring.responseDelayTracking
                          ? 'bg-[#007BFF] justify-end'
                          : 'bg-gray-200 justify-start'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.proctoring.responseDelayTracking}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            proctoring: {
                              ...prev.proctoring,
                              responseDelayTracking: e.target.checked,
                            },
                          }))
                        }
                        className="sr-only"
                      />
                      <span className="h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out" />
                    </span>
                  </label>

                  {/* Speech Pattern Analysis */}
                  <label className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:border-[#007BFF]/30 hover:bg-[#007BFF]/[0.02] cursor-pointer group">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-gray-900 block">Speech Pattern Analysis</span>
                      <span className="text-xs text-gray-500 mt-1 block">
                        Analyzes speech patterns, pacing, and naturalness to detect if responses sound scripted, read from notes, or generated by AI tools.
                      </span>
                    </div>
                    <span
                      className={`inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full p-0.5 cursor-pointer border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-[#007BFF] focus-within:ring-offset-2 ${formData.proctoring.speechPatternAnalysis
                          ? 'bg-[#007BFF] justify-end'
                          : 'bg-gray-200 justify-start'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.proctoring.speechPatternAnalysis}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            proctoring: {
                              ...prev.proctoring,
                              speechPatternAnalysis: e.target.checked,
                            },
                          }))
                        }
                        className="sr-only"
                      />
                      <span className="h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out" />
                    </span>
                  </label>

                  {/* Transcript Analysis */}
                  <label className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:border-[#007BFF]/30 hover:bg-[#007BFF]/[0.02] cursor-pointer group">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-gray-900 block">Transcript Analysis</span>
                      <span className="text-xs text-gray-500 mt-1 block">
                        Examines transcript for suspicious patterns like copied text, AI-generated language, or inconsistencies that suggest cheating.
                      </span>
                    </div>
                    <span
                      className={`inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full p-0.5 cursor-pointer border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-[#007BFF] focus-within:ring-offset-2 ${formData.proctoring.transcriptAnalysis
                          ? 'bg-[#007BFF] justify-end'
                          : 'bg-gray-200 justify-start'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.proctoring.transcriptAnalysis}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            proctoring: {
                              ...prev.proctoring,
                              transcriptAnalysis: e.target.checked,
                            },
                          }))
                        }
                        className="sr-only"
                      />
                      <span className="h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out" />
                    </span>
                  </label>

                  {/* Multi-Speaker Detection */}
                  <label className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:border-[#007BFF]/30 hover:bg-[#007BFF]/[0.02] cursor-pointer group">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-gray-900 block">Multi-Speaker Detection</span>
                      <span className="text-xs text-gray-500 mt-1 block">
                        Identifies when multiple voices are detected in audio, indicating that someone else may be helping candidate during interview.
                      </span>
                    </div>
                    <span
                      className={`inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full p-0.5 cursor-pointer border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-[#007BFF] focus-within:ring-offset-2 ${formData.proctoring.multiSpeakerDetection
                          ? 'bg-[#007BFF] justify-end'
                          : 'bg-gray-200 justify-start'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.proctoring.multiSpeakerDetection}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            proctoring: {
                              ...prev.proctoring,
                              multiSpeakerDetection: e.target.checked,
                            },
                          }))
                        }
                        className="sr-only"
                      />
                      <span className="h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out" />
                    </span>
                  </label>

                  {/* Voice Consistency Check */}
                  <label className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:border-[#007BFF]/30 hover:bg-[#007BFF]/[0.02] cursor-pointer group">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-gray-900 block">Voice Consistency Check</span>
                      <span className="text-xs text-gray-500 mt-1 block">
                        Verifies that same voice is speaking throughout interview by analyzing voice characteristics and detecting voice changes.
                      </span>
                    </div>
                    <span
                      className={`inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full p-0.5 cursor-pointer border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-[#007BFF] focus-within:ring-offset-2 ${formData.proctoring.voiceConsistencyCheck
                          ? 'bg-[#007BFF] justify-end'
                          : 'bg-gray-200 justify-start'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.proctoring.voiceConsistencyCheck}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            proctoring: {
                              ...prev.proctoring,
                              voiceConsistencyCheck: e.target.checked,
                            },
                          }))
                        }
                        className="sr-only"
                      />
                      <span className="h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out" />
                    </span>
                  </label>

                  {/* Random Interrupt Questions */}
                  <label className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:border-[#007BFF]/30 hover:bg-[#007BFF]/[0.02] cursor-pointer group">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-gray-900 block">Random Interrupt Questions</span>
                      <span className="text-xs text-gray-500 mt-1 block">
                        Periodically interrupts with unexpected follow-up questions to verify candidate is actively listening and responding naturally, not reading from notes.
                      </span>
                    </div>
                    <span
                      className={`inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full p-0.5 cursor-pointer border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-[#007BFF] focus-within:ring-offset-2 ${formData.proctoring.randomInterruptQuestions
                          ? 'bg-[#007BFF] justify-end'
                          : 'bg-gray-200 justify-start'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.proctoring.randomInterruptQuestions}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            proctoring: {
                              ...prev.proctoring,
                              randomInterruptQuestions: e.target.checked,
                            },
                          }))
                        }
                        className="sr-only"
                      />
                      <span className="h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out" />
                    </span>
                  </label>

                  {/* Video Proctoring: Face Presence */}
                  <label className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:border-[#007BFF]/30 hover:bg-[#007BFF]/[0.02] cursor-pointer group">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-gray-900 block">
                        Face Presence Detection (Video)
                      </span>
                      <span className="text-xs text-gray-500 mt-1 block">
                        Uses the candidate&apos;s camera feed to verify that a face remains visible during the interview. Flags long periods when the candidate moves away from the camera.
                      </span>
                    </div>
                    <span
                      className={`inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full p-0.5 cursor-pointer border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-[#007BFF] focus-within:ring-offset-2 ${formData.proctoring.facePresenceDetection ? 'bg-[#007BFF] justify-end' : 'bg-gray-200 justify-start'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.proctoring.facePresenceDetection}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            proctoring: {
                              ...prev.proctoring,
                              facePresenceDetection: e.target.checked,
                            },
                          }))
                        }
                        className="sr-only"
                      />
                      <span className="h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out" />
                    </span>
                  </label>

                  {/* Video Proctoring: Multiple Faces */}
                  <label className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:border-[#007BFF]/30 hover:bg-[#007BFF]/[0.02] cursor-pointer group">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-gray-900 block">
                        Multiple Face Detection (Video)
                      </span>
                      <span className="text-xs text-gray-500 mt-1 block">
                        Detects when more than one face appears in the camera frame, indicating that someone else might be assisting the candidate.
                      </span>
                    </div>
                    <span
                      className={`inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full p-0.5 cursor-pointer border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-[#007BFF] focus-within:ring-offset-2 ${formData.proctoring.multipleFaceDetection ? 'bg-[#007BFF] justify-end' : 'bg-gray-200 justify-start'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.proctoring.multipleFaceDetection}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            proctoring: {
                              ...prev.proctoring,
                              multipleFaceDetection: e.target.checked,
                            },
                          }))
                        }
                        className="sr-only"
                      />
                      <span className="h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out" />
                    </span>
                  </label>

                  {/* Video Proctoring: Face Identity Consistency */}
                  <label className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:border-[#007BFF]/30 hover:bg-[#007BFF]/[0.02] cursor-pointer group">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-gray-900 block">
                        Face Identity Consistency (Video)
                      </span>
                      <span className="text-xs text-gray-500 mt-1 block">
                        Captures a baseline face signature at the start and compares later frames to ensure the same person remains in front of the camera.
                      </span>
                    </div>
                    <span
                      className={`inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full p-0.5 cursor-pointer border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-[#007BFF] focus-within:ring-offset-2 ${formData.proctoring.faceIdentityConsistency ? 'bg-[#007BFF] justify-end' : 'bg-gray-200 justify-start'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.proctoring.faceIdentityConsistency}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            proctoring: {
                              ...prev.proctoring,
                              faceIdentityConsistency: e.target.checked,
                            },
                          }))
                        }
                        className="sr-only"
                      />
                      <span className="h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out" />
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Step 5: Interactive Demo Preview */}
            {currentStep === 5 && (
              <div className="h-full flex flex-col">
                {/* Coming Soon Message */}
                <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#007BFF] to-[#0062CC] rounded-full flex items-center justify-center mb-4 shadow-lg">
                    <PlayIcon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Demo Preview</h3>
                  <p className="text-gray-500 mb-6 max-w-md">
                    The interactive demo preview feature is coming soon. You'll be able to preview
                    the interview experience exactly as candidates will see it.
                  </p>
                  <div className="px-4 py-2 bg-[#007BFF]/10 text-[#0062CC] rounded-lg font-medium">
                    Coming Soon
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Navigation — no Back on step 2 (Edit), like Assessment */}
          <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white">
            <div className="flex items-center justify-between">
              {currentStep > 1 && currentStep !== 2 ? (
                <button
                  type="button"
                  onClick={() => handleStepNavigation(currentStep - 1)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all"
                >
                  Back
                </button>
              ) : currentStep === 1 && enableCreationModeSelection ? (
                <button
                  type="button"
                  onClick={() => {
                    setCreationMode(null);
                    setCurrentStep(0);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all"
                >
                  Back
                </button>
              ) : (
                <div />
              )}

              <div className="flex items-center gap-4">
                {/* Generating Questions Indicator - Bottom Right */}
                {isAutoGenerating && generatingQuestions.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-[#007BFF]">
                    <SparklesIcon className="w-4 h-4 animate-pulse" />
                    <span className="font-medium">
                      Generating question {generatingQuestions.length}...
                    </span>
                  </div>
                )}

                {/* Step 1: Generate/Create questions */}
                {currentStep === 1 && (
                  <button
                    type="button"
                    data-testid="generate-questions-btn"
                    onClick={() => handleStepNavigation(2, { fromGenerateCta: true })}
                    disabled={
                      !formData.title ||
                      !formData.category ||
                      (isContextRequired ? !contextValue : false) ||
                      // In library + manual flow we don't require tags; otherwise we do (except for rubric-based).
                      ((!isLibraryCreateFlow || creationMode !== CREATION_MODE.MANUAL) && advancedType !== ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED
                        ? formData.selectedTags.length === 0
                        : false) ||
                      (isLibraryCreateFlow &&
                      creationMode === CREATION_MODE.MANUAL
                        ? !questionFile
                        : false) ||
                      isAutoGenerating
                    }
                    className="px-5 py-2 text-sm font-medium text-white bg-[#007BFF] rounded-md hover:bg-[#0062CC] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-all shadow-sm"
                  >
                    {isAutoGenerating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Generating Questions...
                      </>
                    ) : (
                      <>
                        {advancedType === ADVANCED_INTERVIEW_TYPES.RUBRIC_BASED
                          ? 'Next step'
                          : creationMode === CREATION_MODE.MANUAL
                          ? 'Create questions'
                          : 'Generate questions'}
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </>
                    )}
                  </button>
                )}

                {/* Step 2: Edit Questions - Continue to Proctoring (with optional save-to-library) */}
                {currentStep === 2 && (
                  <button
                    type="button"
                    data-testid="continue-to-proctoring-btn"
                    onClick={() => {
                      if (isEditLibrary) {
                        requestLibraryEditSave(handleSaveLibraryEdit);
                      } else if (isLibraryCreateFlow) {
                        handleSaveToLibrary();
                      } else {
                        if (!hasSavedToLibrary && formData.questions.length > 0) {
                          setShowSaveToLibraryConfirm(true);
                        } else {
                          handleStepNavigation(3);
                        }
                      }
                    }}
                    disabled={savingToLibrary || editLibrarySaving || isAutoGenerating}
                    className="px-5 py-2 text-sm font-medium text-white bg-[#007BFF] rounded-md hover:bg-[#0062CC] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-all shadow-sm"
                  >
                    {savingToLibrary || editLibrarySaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Saving...
                      </>
                    ) : isEditLibrary ? (
                      'Save Changes'
                    ) : isLibraryCreateFlow ? (
                      'Save to AI Interview Library'
                    ) : (
                      <>
                        Continue to Proctoring
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </>
                    )}
                  </button>
                )}

                {/* Step 3: Proctoring - Continue to Invite or Create */}
                {currentStep === 3 &&
                  (skipInviteStep ? (
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={
                        loading ||
                        formData.questions.length === 0 ||
                        isGenerating ||
                        isAutoGenerating
                      }
                      data-testid="create-interview-btn-1"
                      className="px-5 py-2 text-sm font-medium text-white bg-[#007BFF] rounded-md hover:bg-[#0062CC] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-all shadow-sm"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <CheckIcon className="w-4 h-4" />
                          Create interview
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleStepNavigation(4)}
                      disabled={formData.questions.length === 0 || isGenerating || isAutoGenerating}
                      className="px-5 py-2 text-sm font-medium text-white bg-[#007BFF] rounded-md hover:bg-[#0062CC] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-all shadow-sm"
                    >
                      Continue to Invite
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  ))}

                {/* Step 4: Invite - Continue to Demo or Create */}
                {currentStep === 4 && (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleStepNavigation(5)}
                      disabled={formData.questions.length === 0}
                      className="px-4 py-2 text-sm font-medium text-[#007BFF] border border-[#007BFF]/20 rounded-md hover:bg-[#007BFF]/5 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
                    >
                      <PlayIcon className="w-4 h-4" />
                      Preview Demo
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={loading || formData.questions.length === 0}
                      data-testid="create-interview-btn-2"
                      className="px-5 py-2 text-sm font-medium text-white bg-[#007BFF] rounded-md hover:bg-[#0062CC] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-all shadow-sm"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Creating...
                        </>
                      ) : (
                        <>
                          <CheckIcon className="w-4 h-4" />
                          Create Interview
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Step 5: Demo Preview - Create Interview */}
                {currentStep === 5 && (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading || formData.questions.length === 0}
                    data-testid="create-interview-btn-3"
                    className="px-5 py-2 text-sm font-medium text-white bg-[#007BFF] rounded-md hover:bg-[#0062CC] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-all shadow-sm"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <CheckIcon className="w-4 h-4" />
                        Create Interview
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  // Non-modal layout (fallback - simple centered form)
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-6">Create AI Interview</h1>
          <p className="text-gray-500 mb-6">
            This page works best when accessed from a job posting.
          </p>
          <button
            onClick={() => navigate('/dashboard/ai-interviews')}
            className="px-4 py-2 text-sm font-medium text-[#007BFF] hover:bg-[#007BFF]/5 rounded-lg transition-colors"
          >
            Go to AI Interviews
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIInterviewCreate;
